<script setup>

import {ref, inject, computed, toRefs, reactive, onMounted} from 'vue';
import StaticWidget from "@/components/common/other/StaticWidget";
import DimensionsEntry from "@/components/common/other/DimensionsEntry";
import MultiSearchDropDown from "@/components/common/input/MultiSearchDropDown";
import DropDownTextArea from "@/components/common/input/DropDownTextArea";
import MovingTooltip from "@/components/common/other/MovingTooltip";
import Button from "@/components/common/input/Button";
import SectionTile from "@/components/bookings/SectionTile";
import CheckBox from "@/components/common/input/CheckBox";
import DateInput from "@/components/common/input/DateInput";
import NumberOnlyInput from "@/components/common/input/NumberOnlyInput";
import StaticTooltip from "@/components/common/other/StaticTooltip";
import BookingSummaryDisplay from "@/components/bookings/BookingSummaryDisplay";
import Tabs from "@/components/common/navigation/tabs/Tabs";
import TabsContent from "@/components/common/navigation/tabs/TabsContent";
import ContainerMultiEntry from "@/components/common/other/ContainerMultiEntry"
import Notification from "@/classes/component-classes/Notification";
import ContactDetail from "@/classes/bookings/ContactDetail";

import {useGetValidatedFields} from "@/composables/common/useGetValidatedFields";
import {useValidateRequiredFields} from "@/composables/common/useValidateRequiredFields";
import {useCheckFieldsForData} from "@/composables/common/useCheckFieldsForData";
import {useCreateSubstringText} from "@/composables/common/useCreateSubstringText";
import {useTimeGenerator} from "@/composables/common/useTimeGenerator";
import {useMapBookingSave} from "@/composables/bookings/useMapBookingSave";

import {useStore} from "vuex";
import {useRoute, useRouter} from "vue-router";
import API from "@/classes/global/API";
import {useGetChargeableWeight} from "@/composables/bookings/useGetChargeableWeight";
import {useMapShipperAddress} from "@/composables/bookings/useMapShipperAddress";
import {useClearShipperContact} from "@/composables/bookings/useClearShipperContact";
import {useMapConsigneeAddress} from "@/composables/bookings/useMapConsigneeAddress";
import {useClearConsigneeContact} from "@/composables/bookings/useClearConsigneeContact";
import {useMapShipperContact} from "@/composables/bookings/useMapShipperContact";
import {useMapConsigneeContact} from "@/composables/bookings/useMapConsigneeContact";
import {useMapShipperSave} from "@/composables/bookings/useMapShipperSave";
import {useMapConsigneeSave} from "@/composables/bookings/useMapConsigneeSave";
import {useProcessUploadedFiles} from "@/composables/common/useProcessUploadFiles";
import {useTileSelection} from "@/composables/bookings/useTileSelection";
import {useClearCompany} from "@/composables/bookings/useClearCompany";
import {useValidateContactEmail} from "@/composables/bookings/useValidateContactEmail";

import PreJobAirBooking from "@/classes/viewmodels/PreJobAirBooking";
import PreJobSeaBooking from "@/classes/viewmodels/PreJobSeaBooking";
import PreJobRoadBooking from "@/classes/viewmodels/PreJobRoadBooking";
import PreJobRailBooking from "@/classes/viewmodels/PreJobRailBooking";
import PreJobAirBookingValidation from "@/classes/bookings/PreJobAirBookingValidation";
import PreJobSeaBookingValidation from "@/classes/bookings/PreJobSeaBookingValidation";
import PreJobRoadBookingValidation from "@/classes/bookings/PreJobRoadBookingValidation";
import PreJobRailBookingValidation from "@/classes/bookings/PreJobRoadBookingValidation";

import {useMapContact} from "@/composables/bookings/useMapContact";
import {useValidateContact} from "@/composables/bookings/useValidateContact";
import {useNewContactMessage} from "@/composables/bookings/useNewContactMessage";
import Modal from "@/components/common/other/Modal.vue";
import ShipperDetail from "@/classes/bookings/ShipperDetail";
import ShipperAddress from "@/components/common/other/ShipperAddress.vue";
import FreightBookingValidation from "@/classes/bookings/FreightBookingValidation";
import CourierBookingValidation from "@/classes/bookings/CourierBookingValidation";
import ShipperContacts from "@/components/common/other/ShipperContacts.vue";
import ConsigneeAddress from "@/components/common/other/ConsigneeAddress.vue";
import ConsigneeContact from "@/components/common/other/ConsigneeContact.vue";
import ConsigneeDetail from "@/classes/bookings/ConsigneeDetail";


const validationViewModel = inject("validationViewModel");

const store = useStore();
const route = useRoute();
const router = useRouter();

const props = defineProps({

  modelValue: {},
  validationViewModelSection: '',
  mode: '',
  tileColor: ''

})

const emits = defineEmits(['update:modelValue'])

const uploadButton = ref();
const uploadFileSizeError = ref(false);
const emailRegExCheck = /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/

const updateShipperContacts = computed(() => {

  if (props.modelValue.shipper.shipperContactFullName || props.modelValue.shipper.shipperContacts) {
    return Math.random().toString(36).substring(2, 15);
  }
})

const updateConsigneeContacts = computed(() => {

  if (props.modelValue.consignee.consigneeContactFullName || props.modelValue.consignee.consigneeContacts) {
    return Math.random().toString(36).substring(2, 15);
  }
})

const updateFileInput = computed(() => {
  return props.modelValue.files.length
})

let uploadFileSizeErrorMessage = ref();

const getPlaceholder = computed(() => getAddressPlaceholder());

const updateShipperList = computed(() => {
  return props.modelValue.metaData.shippers;
})

const updateConsigneeList = computed(() => {
  return props.modelValue.metaData.consignees;
})

const handleSearch = computed(() => {
  return props.modelValue.customer !== ''
})

const allowWeightEntry = computed(() => {
  return props.modelValue.dimensions.multiLineDims.length > 0;
})

onMounted(() => {

  // This is required to manage the state of the validation object for edit and new bookings.
  // It prevents the validation state being held when coming out of an edit and into a new freight booking.
  // Checks the url for an edit id number to ensure you have just come from the edit screen,
  // then resets validation. If you are going back and forth with a new booking,
  // the original state is held and the validation is not reset.

  router.beforeEach((to, from) => {

    const isEditId = /^\d+$/.test(from.params.id)

    if (isEditId)

      switch (route.params.section) {

        case('Air') :
          validationViewModel.value[props.validationViewModelSection] = new PreJobAirBookingValidation();
          break;
        case('Sea') :
          validationViewModel.value[props.validationViewModelSection] = new PreJobSeaBookingValidation();
          break;
        case('Road') :
          validationViewModel.value[props.validationViewModelSection] = new PreJobRoadBookingValidation();
          break;
        case('Rail') :
          validationViewModel.value[props.validationViewModelSection] = new PreJobRailBookingValidation();
          break;
      }

  })

  prepareMetaData();

})

function clearShipperAndConsigneeData() {

  props.modelValue.metaData.shippers = [];
  props.modelValue.metaData.consignees = [];

  props.modelValue.shipper = new ShipperDetail();
  props.modelValue.consignees = new ConsigneeDetail();

}

function getAddressPlaceholder() {

  return require(`@/assets/address_placeholder.png`);
}

function mapShipperAddress(data) {

  useClearShipperContact(props.modelValue);
  useMapShipperAddress(data, props.modelValue);

}

function mapConsigneeAddress(data) {

  useMapConsigneeAddress(data, props.modelValue);
  useClearConsigneeContact(props.modelValue);

}

function mapSelectedShipperContactDetail(contact) {

  useMapShipperContact(contact, props.modelValue);

}

function mapSelectedConsigneeContactDetail(contact) {

  useMapConsigneeContact(contact, props.modelValue);

}

function mapShipperSaveDetails(isNewContact = false) {

  useMapShipperSave(props.modelValue, route, isNewContact);

}

function mapConsigneeSaveDetails(isNewContact = false) {

  useMapConsigneeSave(props.modelValue, route, isNewContact);

}

function getChargeableWeightCalculation(totalWeight) {

  props.modelValue.unitOfMeasureSwitch ? props.modelValue.dimensions.unitOfMeasure = 'imperial' :
      props.modelValue.dimensions.unitOfMeasure = 'metric';

  const grossWeight = totalWeight.value; // copy computed value
  useGetChargeableWeight(route, grossWeight, props.modelValue);

}

function handleTileSelection(tile) {

  let currentTile = useTileSelection(tile, props.modelValue, validationViewModel);

  checkFields(currentTile[0]);
  checkFields(tile);

}

function handleValidateAndClose(tile) {

  let validationResult = validate(tile);

  if (validationResult) handleTileSelection(tile);

  return validationResult

}

function handleRemoveDocument(index) {

  props.modelValue.files.splice(index, 1);

}

function handleUpload() {

  uploadButton.value.click()
}

function handleClearCompany(area) {

  useClearCompany(area, props, validationViewModel);
}

// function handleShipperSave(isUpdate) {
//
//   let sectionIsValid = validate('shipper');
//   let contactIsValid = validateContact('shipper')
//
//   if (sectionIsValid && contactIsValid && checkShipperEmailValid() && isUpdate && props.modelValue.shipper.shipperContactFullName) {
//
//     let contactToEdit = props.modelValue.shipper.shipperContacts.find(c => c.id === props.modelValue.shipper.shipperContactId);
//
//     useMapContact(contactToEdit, props.modelValue, true, false);
//
//     mapShipperSaveDetails();
//
//     return;
//
//   } else if (contactIsValid && props.modelValue.tileValidation['shipper'] && checkShipperEmailValid()) {
//
//     let newContact = new ContactDetail();
//
//     useMapContact(newContact, props.modelValue, true, true);
//
//     // This is to enable the contactId to be set for the latest added contact
//     mapShipperSaveDetails(true);
//
//     return;
//
//   }
//
//   // This should run if no contact data is entered and the general validation has passed
//   if (props.modelValue.tileValidation['shipper'] && checkShipperEmailValid()) mapShipperSaveDetails();
//
// }

function handleShipperSave() {

  if (validate('shipper')) mapShipperSaveDetails();

}

function handleConsigneeSave(isUpdate) {

  if (validate('consignee'))  mapConsigneeSaveDetails();

}


// function handleConsigneeSave(isUpdate) {
//
//   let sectionIsValid = validate('consignee');
//   let contactIsValid = validateContact('consignee')
//
//   if (sectionIsValid && contactIsValid && checkConsigneeEmailValid() && isUpdate && props.modelValue.consignee.consigneeContactFullName) {
//
//     let contactToEdit = props.modelValue.consignee.consigneeContacts.find(c => c.id === props.modelValue.consignee.consigneeContactId);
//
//     useMapContact(contactToEdit, props.modelValue, false, false);
//
//     mapConsigneeSaveDetails();
//
//     return;
//
//   } else if (contactIsValid && props.modelValue.tileValidation['consignee'] && checkConsigneeEmailValid()) {
//
//     let newContact = new ContactDetail();
//
//     useMapContact(newContact, props.modelValue, false, true)
//
//     mapConsigneeSaveDetails(true);
//
//     return;
//
//   }
//
//   if (props.modelValue.tileValidation['consignee'] && checkConsigneeEmailValid()) mapConsigneeSaveDetails();
// }

function handleNewBooking(mode) {

  router.push({name: 'prejob', params: {owner: store.state.owner, section: mode, id: 'new'}});

  switch (mode) {

    case 'Air' :
      emits('update:modelValue', new PreJobAirBooking());
      break;
    case 'Sea' :
      emits('update:modelValue', new PreJobSeaBooking());
      break;
    case 'Road' :
      emits('update:modelValue', new PreJobRoadBooking());
      break;
    case 'Rail' :
      emits('update:modelValue', new PreJobRailBooking());
      break;
  }

}

function checkShipperEmailValid() {

  return useValidateContactEmail(props.modelValue, true, validationViewModel.value[props.validationViewModelSection]);

}

function checkConsigneeEmailValid() {

  return useValidateContactEmail(props.modelValue, false, validationViewModel.value[props.validationViewModelSection]);

}

function validateContact(tile) {

  return useValidateContact(props.modelValue, tile);

}

function selectBillingPartyId(selection) {

  props.modelValue.shipment.shipmentBillingPartyId = selection.id;
}

function newContactMessage(section) {

  return useNewContactMessage(props.modelValue, section);

}

function allowShipperSaveButton() {

  return !(props.modelValue.shipper.shipperName && props.modelValue.shipper.shipperAddressLine1);

}

function allowConsigneeSaveButton() {

  return !(props.modelValue.consignee.consigneeName && props.modelValue.consignee.consigneeAddressLine1);

}

function checkFields(tile) {

  if (useCheckFieldsForData(props.modelValue[tile])) {

    props.modelValue.tileValidation[tile] = validate(tile);
  }

}

function validate(tile) {

  let fieldsValid = useValidateRequiredFields(props.modelValue[tile],
      validationViewModel.value[props.validationViewModelSection], props.modelValue.metaData.validatedFields);

  props.modelValue.tileValidation[tile] = fieldsValid;

  return fieldsValid

}

function setPortId(section, prop, id) {

  props.modelValue[section][prop] = id;

}

async function validateSubmit() {

  props.modelValue.tileValidation['shipper'] = validate('shipper');
  props.modelValue.tileValidation['consignee'] = validate('consignee');
  props.modelValue.tileValidation['dimensions'] = validate('dimensions');

  if (route.params.section === 'Air') props.modelValue.tileValidation['flightShipment'] = validate('flightShipment');
  if (route.params.section === 'Sea') props.modelValue.tileValidation['seaShipment'] = validate('seaShipment');
  if (route.params.section === 'Road') props.modelValue.tileValidation['roadShipment'] = validate('roadShipment');
  if (route.params.section === 'Rail') props.modelValue.tileValidation['railShipment'] = validate('railShipment');

  for (let each in props.modelValue.tileValidation) {

    if (props.modelValue.tileValidation[each] === false) return false;

  }

  // Validation Passed for all sections
  let response = await API.saveBooking(useMapBookingSave(props.modelValue, props.validationViewModelSection),
      'freightBooking', route, route.params.section);

  if (response) {

    try {

      props.modelValue.id = response.id;
      props.modelValue.jobNumber = response.jobNumber;

      // Add the new returned dims with id's to the model, remove unitOfMeasure as not used for UI and would run through the useCheckFieldsForData method
      // with the value sent back from server (metric / imperial, text) and trigger the tile to show the data present sheild
      props.modelValue.dimensions = {...props.modelValue.dimensions, ...response.dimensions};

      // Ensure always string value for total weight to avoid breaking calcs at server
      props.modelValue.dimensions.totalWeight = props.modelValue.dimensions.totalWeight.toString();
      delete props.modelValue.dimensions.unitOfMeasure;

      if (route.params.section === 'Sea') {

        props.modelValue.seaShipment.containers = response.seaShipment.containers;
      }

      store.state.banners.success = new Notification(true, 'Booking Saved Successfully');
    } catch (error) {

      // TODO Log Error
    }

  }

}

async function prepareMetaData() {

  const response = await API.getShipperMetaData(route, props.validationViewModelSection);

  if (response) props.modelValue.metaData = response;

  // REMOVE AFTER DB UPDATE
  // props.modelValue.metaData.validatedFields = props.modelValue.tempValidFields;

  // Look at altering this from server side, does not need shippers up front
  props.modelValue.metaData.shippers = [];
  props.modelValue.metaData.consignees = [];
}

async function populateDropDownData() {

  let shippers = await API.getShipperListByAccount(route);

  if (shippers) props.modelValue.metaData.shippers = shippers;

  let consignees = await API.getConsigneeListByAccount(route);

  if (consignees) props.modelValue.metaData.consignees = consignees;

}

async function getCompanyDetails(name, section) {

  // Get selected shipper / consignee address details
  props.modelValue[section].id = parseInt(name.id);

  let response = await API.getCompanyDetail(name.id, route)

  if (response) {

    try {

      if (section === 'shipper') mapShipperAddress(response);
      if (section === 'consignee') mapConsigneeAddress(response);

    } catch (error) {

      // TODO Log Error

    }

  }

}

async function handleUploadedFiles(files) {

  uploadFileSizeError.value = false;

  let fileUploadOk = await useProcessUploadedFiles(files, props.modelValue);

  if (!fileUploadOk.value) {

    uploadFileSizeError.value = true;
    uploadFileSizeErrorMessage.value = fileUploadOk.file.name + ' ' + 'is too large to upload - max 28MB'
  }

}

//TODO Refactor these methods into one file for use with freight, courier and agent bookings

function closeShipperModel() {

  validationViewModel.value[props.validationViewModelSection].shipperName.value = false;
  props.modelValue.newShipperCompany = new ShipperDetail();

}

function useUnSavedShipperAddress() {

  if (useValidateRequiredFields(props.modelValue.newShipperCompany,
      validationViewModel.value[props.validationViewModelSection], props.modelValue.metaData.validatedFields)) {

    const addressFields = ['shipperName', 'shipperAddressLine1', 'shipperAddressLine2', 'shipperCity',
      'shipperPostCode', 'shipperCounty', 'shipperCountry', 'shipperVatNumber', 'shipperRef'];

    if(props.modelValue.newShipperCompany.id !== 0) {

      // Must be a selected address record, check for any changes and remove id if so
      addressFields.forEach(field => {

        if (props.modelValue.newShipperCompany[field] !== props.modelValue.shipper[field]) {

          return props.modelValue.newShipperCompany.id = 0;
        }
      })
    }

    props.modelValue.shipper = props.modelValue.newShipperCompany;
    props.modelValue.openShipperModal = false;
  }
}

function handleNewCompanySave(addressType) {

  if (useValidateRequiredFields(props.modelValue[addressType],
      validationViewModel.value[props.validationViewModelSection], props.modelValue.metaData.validatedFields)) {

    if(addressType === 'newShipperCompany') {

      mapNewShipperAddress();
      handleShipperSave();

    } else {

      mapNewConsigneeAddress();
      handleConsigneeSave(false);
    }
  }
}

function addNewShipperAddress() {

  props.modelValue.isEditShipperAddress = false;

  props.validationViewModelSection === 'freightBooking' ?
      validationViewModel.value.freightBooking = new FreightBookingValidation() :
      validationViewModel.value.courierBooking = new CourierBookingValidation();

  props.modelValue.newShipperCompany = new ShipperDetail();
  props.modelValue.openShipperModal = true;
}

function editShipperAddress() {

  props.modelValue.isEditShipperAddress = true;

  props.modelValue.openShipperModal = true;
  props.modelValue.newShipperCompany =  JSON.parse(JSON.stringify(props.modelValue.shipper));
}

function addNewShipperContact() {

  clearContactValidation('shipper');

  useClearShipperContact(props.modelValue.newShipperCompany);

  props.modelValue.openShipperContactModal = true;
}


function clearContactValidation(addressType) {

  if(addressType === 'shipper') {

    validationViewModel.value[props.validationViewModelSection].shipperContactEmail.value = false;
    validationViewModel.value[props.validationViewModelSection].shipperContactFirstName.value = false

  } else {

    validationViewModel.value[props.validationViewModelSection].consigneeContactEmail.value = false;
    validationViewModel.value[props.validationViewModelSection].consigneeContactFirstName.value = false;
  }

}

function useUnSavedShipperContact() {

  let results = validateShipperContact()

  if(results.firstNameValid && results.emailValid) {

    const contactFields = ['shipperContactTitle', 'shipperContactFirstName',
      'shipperContactLastName', 'shipperContactPhone', 'shipperContactEmail', 'shipperContactDepartment'
    ]

    contactFields.forEach(field => {

      if(props.modelValue.newShipperCompany[field] !== props.modelValue.shipper[field]) {

        props.modelValue.newShipperCompany.shipperContactId = 0;
      }

    })

    mapShipperContact();
  }
}

function saveNewShipperContact(isUpdate) {

  let result = validateShipperContact();

  if (result.firstNameValid && result.emailValid) {

    mapShipperContact();

    if(isUpdate) {

      let contactToEdit = props.modelValue.shipper.shipperContacts.find(c => c.id === props.modelValue.shipper.shipperContactId);

      useMapContact(contactToEdit, props.modelValue, true, false);

    } else {

      let newContact = new ContactDetail();

      useMapContact(newContact, props.modelValue, true, true);
    }

    handleShipperSave();
  }
}

function validateShipperContact() {

  let firstNameValid = true;

  validationViewModel.value[props.validationViewModelSection].shipperContactEmail.value = false;

  if (!props.modelValue.newShipperCompany.shipperContactFirstName) {

    validationViewModel.value[props.validationViewModelSection].shipperContactFirstName.value = true;

    firstNameValid = false;
  }

  let emailValid = true;

  if(props.modelValue.newShipperCompany.shipperContactEmail) {

    emailValid = store.state.emailRegExCheck.test(props.modelValue.newShipperCompany.shipperContactEmail);

    validationViewModel.value[props.validationViewModelSection].shipperContactEmail.messageNumber = 1
    validationViewModel.value[props.validationViewModelSection].shipperContactEmail.value = !emailValid;

  }

  return {firstNameValid, emailValid}
}

function closeConsigneeModel() {

  validationViewModel.value[props.validationViewModelSection].consigneeName.value = false;
  props.modelValue.newShipperCompany = new ShipperDetail();

}

function useUnSavedConsigneeAddress() {

  if (useValidateRequiredFields(props.modelValue.newConsigneeCompany,
      validationViewModel.value[props.validationViewModelSection], props.modelValue.metaData.validatedFields)) {

    const addressFields = ['consigneeName', 'consigneeAddressLine1', 'consigneeAddressLine2', 'consigneeCity',
      'consigneePostCode', 'consigneeCounty', 'consigneeCountry', 'consigneeVatNumber'];

    if(props.modelValue.newConsigneeCompany.id !== 0) {

      // Must be a selected address record, check for any changes and remove id if so
      addressFields.forEach(field => {

        if(props.modelValue.newConsigneeCompany.id !== 0) {

          // Must be a selected address record, check for any changes and remove id if so
          addressFields.forEach(field => {

            if (props.modelValue.newConsigneeCompany[field] !== props.modelValue.consignee[field]) {

              return props.modelValue.newConsigneeCompany.id = 0;
            }
          })
        }
      })
    }

    props.modelValue.consignee = props.modelValue.newConsigneeCompany;
    props.modelValue.openConsigneeModal = false;
  }
}

function addNewConsigneeAddress() {

  props.modelValue.isEditConsigneeAddress = false;

  props.validationViewModelSection === 'freightBooking' ?
      validationViewModel.value.freightBooking = new FreightBookingValidation() :
      validationViewModel.value.courierBooking = new CourierBookingValidation();

  props.modelValue.newConsigneeCompany = new ConsigneeDetail();
  props.modelValue.openConsigneeModal = true;
}

function editConsigneeAddress() {

  props.modelValue.isEditConsigneeAddress = true;

  props.modelValue.openConsigneeModal = true;
  props.modelValue.newConsigneeCompany =  JSON.parse(JSON.stringify(props.modelValue.consignee));
}

function addNewConsigneeContact() {

  clearContactValidation('consignee');

  useClearConsigneeContact(props.modelValue.newConsigneeCompany);

  props.modelValue.openConsigneeContactModal = true;
}

function useUnSavedConsigneeContact() {

  let results = validateConsigneeContact();

  if (results.firstNameValid && results.emailValid) {

    const contactFields = ['consigneeContactTitle', 'consigneeContactFirstName',
      'consigneeContactLastName', 'consigneeContactPhone', 'consigneeContactEmail', 'consigneeContactDepartment'
    ]

    contactFields.forEach(field => {

      if(props.modelValue.newConsigneeCompany[field] !== props.modelValue.consignee[field]) {

        props.modelValue.newConsigneeCompany.consigneeContactId = 0;
      }

    })

    mapConsigneeContact();
  }
}

function validateConsigneeContact() {

  let firstNameValid = true;

  validationViewModel.value[props.validationViewModelSection].consigneeContactEmail.value = false;

  if (!props.modelValue.newConsigneeCompany.consigneeContactFirstName) {

    validationViewModel.value[props.validationViewModelSection].consigneeContactFirstName.value = true;

    firstNameValid = false;
  }

  let emailValid = true;

  if (props.modelValue.newConsigneeCompany.consigneeContactEmail) {

    emailValid = store.state.emailRegExCheck.test(props.modelValue.newConsigneeCompany.consigneeContactEmail);

    validationViewModel.value[props.validationViewModelSection].consigneeContactEmail.messageNumber = 1
    validationViewModel.value[props.validationViewModelSection].consigneeContactEmail.value = !emailValid;

  }

  return {firstNameValid, emailValid};
}

function saveNewConsigneeContact(isUpdate) {

  let results = validateConsigneeContact();

  if(results.firstNameValid && results.emailValid) {

    mapConsigneeContact();

    if (isUpdate) {

      let contactToEdit = props.modelValue.consignee.consigneeContacts.find(c => c.id === props.modelValue.consignee.consigneeContactId);

      useMapContact(contactToEdit, props.modelValue, false, false);

    } else {

      let newContact = new ContactDetail();

      useMapContact(newContact, props.modelValue, false, true);
    }

    handleConsigneeSave();
  }
}

function editShipperContact() {

  props.modelValue.openShipperContactModal = true;

  clearContactValidation('shipper');

  props.modelValue.newShipperCompany.shipperContactId = props.modelValue.shipper.shipperContactId;
  props.modelValue.newShipperCompany.shipperContactTitle = props.modelValue.shipper.shipperContactTitle;
  props.modelValue.newShipperCompany.shipperContactFirstName = props.modelValue.shipper.shipperContactFirstName;
  props.modelValue.newShipperCompany.shipperContactLastName = props.modelValue.shipper.shipperContactLastName;
  props.modelValue.newShipperCompany.shipperContactPhone = props.modelValue.shipper.shipperContactPhone;
  props.modelValue.newShipperCompany.shipperContactEmail = props.modelValue.shipper.shipperContactEmail;
  props.modelValue.newShipperCompany.shipperContactDepartment = props.modelValue.shipper.shipperContactDepartment;
}


function mapNewShipperAddress() {

  props.modelValue.shipper.shipperName = props.modelValue.newShipperCompany.shipperName;
  props.modelValue.shipper.shipperRef = props.modelValue.newShipperCompany.shipperRef;
  props.modelValue.shipper.shipperAddressLine1 = props.modelValue.newShipperCompany.shipperAddressLine1;
  props.modelValue.shipper.shipperAddressLine2 = props.modelValue.newShipperCompany.shipperAddressLine2;
  props.modelValue.shipper.shipperCity = props.modelValue.newShipperCompany.shipperCity;
  props.modelValue.shipper.shipperPostcode = props.modelValue.newShipperCompany.shipperPostcode;
  props.modelValue.shipper.shipperCounty = props.modelValue.newShipperCompany.shipperCounty;
  props.modelValue.shipper.shipperCountry = props.modelValue.newShipperCompany.shipperCountry;
  props.modelValue.shipper.shipperVatNumber = props.modelValue.newShipperCompany.shipperVatNumber;
}

function mapShipperContact() {

  props.modelValue.shipper.shipperContactFullName = props.modelValue.newShipperCompany.shipperContactTitle + ' ' +
      props.modelValue.newShipperCompany.shipperContactFirstName + ' '
      + props.modelValue.newShipperCompany.shipperContactLastName;

  props.modelValue.shipper.shipperContactId = props.modelValue.newShipperCompany.shipperContactId;
  props.modelValue.shipper.shipperContactTitle = props.modelValue.newShipperCompany.shipperContactTitle;
  props.modelValue.shipper.shipperContactFirstName = props.modelValue.newShipperCompany.shipperContactFirstName;
  props.modelValue.shipper.shipperContactLastName = props.modelValue.newShipperCompany.shipperContactLastName;
  props.modelValue.shipper.shipperContactPhone = props.modelValue.newShipperCompany.shipperContactPhone;
  props.modelValue.shipper.shipperContactEmail = props.modelValue.newShipperCompany.shipperContactEmail;
  props.modelValue.shipper.shipperContactDepartment = props.modelValue.newShipperCompany.shipperContactDepartment;
}

function mapNewConsigneeAddress() {

  props.modelValue.consignee.consigneeName = props.modelValue.newConsigneeCompany.consigneeName;
  props.modelValue.consignee.consigneeRef = props.modelValue.newConsigneeCompany.consigneeRef;
  props.modelValue.consignee.additionalRef = props.modelValue.newConsigneeCompany.additionalRef;
  props.modelValue.consignee.consigneeAddressLine1 = props.modelValue.newConsigneeCompany.consigneeAddressLine1;
  props.modelValue.consignee.consigneeAddressLine2 = props.modelValue.newConsigneeCompany.consigneeAddressLine2;
  props.modelValue.consignee.consigneeCity = props.modelValue.newConsigneeCompany.consigneeCity;
  props.modelValue.consignee.consigneePostcode = props.modelValue.newConsigneeCompany.consigneePostcode;
  props.modelValue.consignee.consigneeCounty = props.modelValue.newConsigneeCompany.consigneeCounty;
  props.modelValue.consignee.consigneeCountry = props.modelValue.newConsigneeCompany.consigneeCountry;
  props.modelValue.consignee.consigneeVatNumber = props.modelValue.newConsigneeCompany.consigneeVatNumber;

}

function mapConsigneeContact() {

  props.modelValue.consignee.consigneeContactFullName = props.modelValue.newConsigneeCompany.consigneeContactTitle + ' ' +
      props.modelValue.newConsigneeCompany.consigneeContactFirstName + ' '
      + props.modelValue.newConsigneeCompany.consigneeContactLastName;

  props.modelValue.consignee.consigneeContactId = props.modelValue.newConsigneeCompany.consigneeContactId;
  props.modelValue.consignee.consigneeContactTitle = props.modelValue.newConsigneeCompany.consigneeContactTitle;
  props.modelValue.consignee.consigneeContactFirstName = props.modelValue.newConsigneeCompany.consigneeContactFirstName;
  props.modelValue.consignee.consigneeContactLastName = props.modelValue.newConsigneeCompany.consigneeContactLastName;
  props.modelValue.consignee.consigneeContactPhone = props.modelValue.newConsigneeCompany.consigneeContactPhone;
  props.modelValue.consignee.consigneeContactEmail = props.modelValue.newConsigneeCompany.consigneeContactEmail;
}

function editConsigneeContact() {

  props.modelValue.openConsigneeContactModal = true;

  clearContactValidation('consignee');

  props.modelValue.newConsigneeCompany.consigneeContactId = props.modelValue.consignee.consigneeContactId;
  props.modelValue.newConsigneeCompany.consigneeContactTitle = props.modelValue.consignee.consigneeContactTitle;
  props.modelValue.newConsigneeCompany.consigneeContactFirstName = props.modelValue.consignee.consigneeContactFirstName;
  props.modelValue.newConsigneeCompany.consigneeContactLastName = props.modelValue.consignee.consigneeContactLastName;
  props.modelValue.newConsigneeCompany.consigneeContactPhone = props.modelValue.consignee.consigneeContactPhone;
  props.modelValue.newConsigneeCompany.consigneeContactEmail = props.modelValue.consignee.consigneeContactEmail;
}

</script>

<template class="cmp-multi-booking">
  <div>
    <StaticWidget
        :title="`Agent Booking ${mode} Details`"
        height="735">
      <template #extra-content>
        <div class="cmp-multi-booking_required-field-message"><span class="global-required-field"></span>
          indicates the field is required
        </div>
        <div v-if="/^\d+$/.test(route.params.id)" class="cmp-multi-booking_edit-message">
          <img height="20" width="20" src="@/assets/icons/akar/akar-icons--edit.png">
          <span style="font-size: 16px;">Edit Booking #<span>{{ route.params.id }}</span></span>
        </div>
        <div v-else-if="props.modelValue.id">
          <span style="font-weight: normal">Job Number</span> #<span>{{ props.modelValue.id }}</span>
        </div>
      </template>
      <template #right-side>
        <div>
          <Button
              v-if="props.modelValue.id === 0"
              text="Save & Submit"
              @click="validateSubmit(props.validationViewModelSection)">
          </Button>
          <Button
              v-if="props.modelValue.id !== 0"
              text="Update"
              type="success"
              @click="validateSubmit(props.validationViewModelSection)">
          </Button>
          <Button
              v-if="props.modelValue.id !== 0"
              text="New Booking"
              @click="handleNewBooking(route.params.section)"
              type="toolbar"></Button>
        </div>
      </template>
      <div class="cmp-multi-booking_wrapper">
        <div class="cmp-multi-booking_top-bar">
          <MultiSearchDropDown
              :key="props.modelValue.metaData.customers"
              :requiredField="false"
              :allowCustomValue="false"
              :useServerSearch="false"
              :disableFilter="true"
              @select="populateDropDownData"
              @clear="clearShipperAndConsigneeData"
              placeholder="Select...."
              :width="400"
              v-model:validation-fail="validationViewModel[props.validationViewModelSection].customer"
              label="Customer"
              :labelWidth="70"
              v-model="props.modelValue.customer"
              :selectPrimitiveValue="true"
              :data-source="props.modelValue.metaData.customers">
          </MultiSearchDropDown>
        </div>
        <div class="cmp-multi-booking_tile-wrapper">
          <SectionTile
              @click="handleTileSelection('shipper')"
              :tileColor="tileColor"
              v-model:viewModel="props.modelValue.shipper"
              v-model:tileSelectionModel="props.modelValue.tileSelection.shipper"
              v-model:validationProp="props.modelValue.tileValidation.shipper">
            <template #icon><img class="global-tile-icon" src="@/assets/icons/shippers_globe.png"></template>
            <template #title>Shipper</template>
            <template #description>Address & Contact</template>
          </SectionTile>
          <SectionTile
              @click="handleTileSelection('consignee')"
              :tileColor="tileColor"
              v-model:viewModel="props.modelValue.consignee"
              v-model:tileSelectionModel="props.modelValue.tileSelection.consignee"
              v-model:validationProp="props.modelValue.tileValidation.consignee">
            <template #icon><img class="global-tile-icon" src="@/assets/icons/consignee_clipboard.png"></template>
            <template #title>Consignee</template>
            <template #description>Address & Contact</template>
          </SectionTile>
          <SectionTile
              v-if="route.params.section === 'Air'"
              :tileColor="tileColor"
              v-model:tileSelectionModel="props.modelValue.tileSelection.flightShipment"
              v-model:validationProp="props.modelValue.tileValidation.flightShipment"
              v-model:viewModel="props.modelValue.customsSelect"
              @click="handleTileSelection('flightShipment')">
            <template #icon><img class="global-tile-icon" src="@/assets/icons/flight_take_off.png"></template>
            <template #title>Flight & Shipment</template>
            <template #description>Movement & Numbers</template>
          </SectionTile>
          <SectionTile
              v-if="route.params.section === 'Sea'"
              :tileColor="tileColor"
              v-model:tileSelectionModel="props.modelValue.tileSelection.seaShipment"
              v-model:validationProp="props.modelValue.tileValidation.seaShipment"
              v-model:viewModel="props.modelValue.seaShipment"
              @click="handleTileSelection('seaShipment')">
            <template #icon><img class="global-tile-icon" src="@/assets/icons/ship.png"></template>
            <template #title>Vessel & Shipment</template>
            <template #description>Movement & Numbers</template>
          </SectionTile>
          <SectionTile
              v-if="route.params.section === 'Road'"
              :tileColor="tileColor"
              v-model:tileSelectionModel="props.modelValue.tileSelection.roadShipment"
              v-model:validationProp="props.modelValue.tileValidation.roadShipment"
              v-model:viewModel="props.modelValue.roadShipment"
              @click="handleTileSelection('roadShipment')">
            <template #icon><img class="global-tile-icon" src="@/assets/icons/truck.png"></template>
            <template #title>Road & Shipment</template>
            <template #description>Movement & Numbers</template>
          </SectionTile>
          <SectionTile
              v-if="route.params.section === 'Rail'"
              :tileColor="tileColor"
              v-model:tileSelectionModel="props.modelValue.tileSelection.railShipment"
              v-model:validationProp="props.modelValue.tileValidation.railShipment"
              v-model:viewModel="props.modelValue.railShipment"
              @click="handleTileSelection('railShipment')">
            <template #icon><img class="global-tile-icon" src="@/assets/icons/rail_lge.png"></template>
            <template #title>Rail & Shipment Detail</template>
            <template #description>Movement & Numbers</template>
          </SectionTile>
          <SectionTile
              @click="handleTileSelection('dimensions')"
              :tileColor="tileColor"
              v-model:viewModel="props.modelValue.dimensions"
              v-model:tileSelectionModel="props.modelValue.tileSelection.dimensions"
              v-model:validationProp="props.modelValue.tileValidation.dimensions">
            <template #icon><img class="global-tile-icon" src="@/assets/icons/dimensions.png"></template>
            <template #title>Dimensions</template>
            <template #description>Packaging & Item Detail</template>
          </SectionTile>
          <SectionTile
              v-model:tileSelectionModel="props.modelValue.tileSelection.documents"
              :tileColor="tileColor"
              @click="handleTileSelection('documents')" :useIcons="false">
            <template #icon><img class="global-tile-icon" src="@/assets/icons/upload_docs.png"></template>
            <template #title>Upload Docs</template>
            <template #description>Add Docs To The Booking</template>
          </SectionTile>
        </div>
        <div v-show="props.modelValue.tileSelection.shipper" class="cmp-multi-booking_drop-down">
          <div class="cmp-multi-booking_input-container-address cmp-multi-booking_add-border">
            <div class="cmp-multi-booking_address-layout-wrapper">
              <ShipperAddress
                  @addNewShipperAddress="addNewShipperAddress()"
                  @editShipperAddress="editShipperAddress()"
                  v-model:validationViewModel="validationViewModel[props.validationViewModelSection].shipperName"
                  v-model="props.modelValue">
              </ShipperAddress>
            </div>
            <div class="cmp-multi-booking_address-layout-wrapper">
              <img height="28" width="28" src="@/assets/icons/akar/akar-icons--people-group.png">
            </div>
            <div class="cmp-multi-booking_address-layout-wrapper">
              <shipperContacts
                  @addNewShipperContact="addNewShipperContact()"
                  @editShipperContact="editShipperContact()"
                  v-model:updateShipperContacts="updateShipperContacts"
                  v-model:validationViewModel="validationViewModel[props.validationViewModelSection].shipperContactFullName"
                  v-model="props.modelValue">
              </shipperContacts>
            </div>
          </div>
        </div>
        <div v-show="props.modelValue.tileSelection.consignee" class="cmp-multi-booking_drop-down">
          <div class="cmp-multi-booking_input-container-address cmp-multi-booking_add-border">
            <div class="cmp-multi-booking_address-layout-wrapper">
              <ConsigneeAddress
                  @addNewConsigneeAddress="addNewConsigneeAddress()"
                  @editConsigneeAddress="editConsigneeAddress()"
                  v-model:validationViewModel="validationViewModel[props.validationViewModelSection].consigneeName"
                  v-model="props.modelValue">
              </ConsigneeAddress>
            </div>
            <div class="cmp-multi-booking_address-layout-wrapper">
              <img height="28" width="28" src="@/assets/icons/akar/akar-icons--people-group.png">
            </div>
            <div class="cmp-multi-booking_address-layout-wrapper">
              <ConsigneeContact
                  @addNewConsigneeContact="addNewConsigneeContact()"
                  @editConsigneeContact="editConsigneeContact()"
                  v-model:updateConsigneeContacts="updateConsigneeContacts"
                  v-model:validationViewModel="validationViewModel[props.validationViewModelSection].consigneeContactFullName"
                  v-model="props.modelValue">
              </ConsigneeContact>
            </div>
          </div>
        </div>
        <div v-if="props.modelValue.tileSelection.flightShipment" class="cmp-multi-booking_drop-down">
          <div class="cmp-multi-booking_input-container cmp-multi-booking_add-border">
            <i class="fa-thin fa-plane-departure cmp-multi-booking_tile-icon"></i>
            <div class="global-css_vertical-form-layout">
              <MultiSearchDropDown
                  :width="300"
                  :labelWidth="110"
                  :transport-mode="route.params.section"
                  :requiredField="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'placeOfReceipt' )"
                  :allowCustomValue="false"
                  :selectPrimitiveValue="true"
                  :useServerSearch="true"
                  @select="setPortId('flightShipment','placeOfReceiptId', $event.id)"
                  searchType="unlocode"
                  placeholder="Search UNLOCODE...."
                  v-model:validation-fail="validationViewModel[props.validationViewModelSection].placeOfReceipt"
                  label="Place Of Receipt"
                  v-model="props.modelValue.flightShipment.placeOfReceipt">
              </MultiSearchDropDown>
              <MultiSearchDropDown
                  :width="300"
                  :labelWidth="110"
                  :transport-mode="route.params.section"
                  :requiredField="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'originPort' )"
                  :allowCustomValue="false"
                  :selectPrimitiveValue="true"
                  :useServerSearch="true"
                  @select="setPortId('flightShipment','originPortId', $event.id)"
                  searchType="iata"
                  placeholder="Search IATA code...."
                  v-model:validation-fail="validationViewModel[props.validationViewModelSection].originPort"
                  label="Origin Port"
                  v-model="props.modelValue.flightShipment.originPort">
              </MultiSearchDropDown>
              <MultiSearchDropDown
                  :width="300"
                  :labelWidth="110"
                  :transport-mode="route.params.section"
                  :requiredField="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'destinationPort' )"
                  :allowCustomValue="false"
                  :selectPrimitiveValue="true"
                  :useServerSearch="true"
                  @select="setPortId('flightShipment','destinationPortId', $event.id)"
                  searchType="iata"
                  placeholder="Search IATA code...."
                  v-model:validation-fail="validationViewModel[props.validationViewModelSection].destinationPort"
                  label="Destination Port"
                  v-model="props.modelValue.flightShipment.destinationPort">
              </MultiSearchDropDown>
              <MultiSearchDropDown
                  :width="300"
                  :labelWidth="110"
                  :transport-mode="route.params.section"
                  :requiredField="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'finalDestination' )"
                  :allowCustomValue="false"
                  :selectPrimitiveValue="true"
                  :useServerSearch="true"
                  @select="setPortId('flightShipment','finalDestinationId', $event.id)"
                  searchType="unlocode"
                  placeholder="Search UNLOCODE...."
                  v-model:validation-fail="validationViewModel[props.validationViewModelSection].finalDestination"
                  label="Final Destination"
                  v-model="props.modelValue.flightShipment.finalDestination">
              </MultiSearchDropDown>
              <TextInput
                  :width="300"
                  :labelWidth="110"
                  v-model="props.modelValue.flightShipment.awbMaster"
                  v-model:validation-fail="validationViewModel[props.validationViewModelSection].awbMaster"
                  :requiredField="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'awbMaster' )"
                  label="AWB Master">
              </TextInput>
              <TextInput
                  :width="300"
                  :labelWidth="110"
                  v-model="props.modelValue.flightShipment.ucrNumber"
                  v-model:validation-fail="validationViewModel[props.validationViewModelSection].ucrNumber"
                  :requiredField="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'ucrNumber' )"
                  label="UCR Number">
              </TextInput>
              <TextInput
                  :width="300"
                  :labelWidth="110"
                  v-model="props.modelValue.flightShipment.hawb"
                  v-model:validation-fail="validationViewModel[props.validationViewModelSection].hawb"
                  :requiredField="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'hawb' )"
                  label="HAWB Number">
              </TextInput>
              <CheckBox
                  v-model="props.modelValue.flightShipment.directFlight"
                  label="Direct Flight"
                  :labelWidth="100">
              </CheckBox>
            </div>
            <div class="global-css_vertical-form-layout">
              <TextInput
                  :width="380"
                  :labelWidth="128"
                  v-model="props.modelValue.flightShipment.departingFlightNumber"
                  v-model:validation-fail="validationViewModel[props.validationViewModelSection].departingFlightNumber"
                  :requiredField="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'departingFlightNumber' )"
                  label="Departing Flight No.">
              </TextInput>
              <TextInput
                  :width="380"
                  :labelWidth="128"
                  v-model="props.modelValue.flightShipment.arrivingFlightNumber"
                  v-model:validation-fail="validationViewModel[props.validationViewModelSection].arrivingFlightNumber"
                  :requiredField="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'arrivingFlightNumber' )"
                  label="Arriving Flight No.">
              </TextInput>
              <TextInput
                  :width="380"
                  :labelWidth="128"
                  v-model="props.modelValue.flightShipment.customerRef"
                  v-model:validation-fail="validationViewModel[props.validationViewModelSection].customerRef"
                  :requiredField="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'customerRef' )"
                  label="Customer Ref">
              </TextInput>
              <MultiSearchDropDown
                  :width="380"
                  :labelWidth="128"
                  :requiredField="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'terms' )"
                  :allowCustomValue="false"
                  :selectPrimitiveValue="true"
                  placeholder="Search or Select...."
                  v-model:validation-fail="validationViewModel[props.validationViewModelSection].terms"
                  label="Terms"
                  v-model="props.modelValue.flightShipment.terms"
                  :data-source="props.modelValue.metaData.terms">
              </MultiSearchDropDown>
              <MultiSearchDropDown
                  :width="380"
                  :labelWidth="128"
                  :requiredField="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'serviceType' )"
                  :allowCustomValue="false"
                  :selectPrimitiveValue="true"
                  placeholder="Search or Select...."
                  v-model:validation-fail="validationViewModel[props.validationViewModelSection].serviceType"
                  label="Service Type"
                  v-model="props.modelValue.flightShipment.serviceType"
                  :data-source="props.modelValue.metaData.serviceTypes">
              </MultiSearchDropDown>
              <div class="global-flex-layout">
                <TextInput
                    :width="75"
                    :labelWidth="128"
                    v-model="props.modelValue.flightShipment.shipmentValue"
                    v-model:validation-fail="validationViewModel[props.validationViewModelSection].shipmentValue"
                    :requiredField="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'shipmentValue' )"
                    label="Value">
                </TextInput>
                <MultiSearchDropDown
                    :width="228"
                    :labelWidth="62"
                    :requiredField="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'currency' )"
                    :allowCustomValue="false"
                    :selectPrimitiveValue="true"
                    placeholder="Search or Select...."
                    v-model:validation-fail="validationViewModel[props.validationViewModelSection].currency"
                    label="Currency"
                    v-model="props.modelValue.flightShipment.currency"
                    :data-source="props.modelValue.metaData.currencies">
                </MultiSearchDropDown>
              </div>
              <DropDownTextArea
                  :width="380"
                  :labelWidth="128"
                  :maxLength="42"
                  :requiredField="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'commodity' )"
                  v-model:validation-fail="validationViewModel[props.validationViewModelSection].commodity"
                  v-model="props.modelValue.flightShipment.commodity"
                  label="Commodity">
              </DropDownTextArea>
              <DropDownTextArea
                  :width="380"
                  :labelWidth="128"
                  :maxLength="42"
                  :requiredField="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'remarks' )"
                  v-model:validation-fail="validationViewModel[props.validationViewModelSection].remarks"
                  v-model="props.modelValue.flightShipment.remarks"
                  label="Additional Remarks">
              </DropDownTextArea>
            </div>
            <div class="cmp-multi-booking_date-area">
              <div class="global-flex-layout cmp-multi-booking_date-area-headings">
                <div>Estimated Dates</div>
                <div>Actual Dates</div>
              </div>
              <div class="global-flex-layout" style="margin-top: 35px;">
                <DateInput
                    v-model="props.modelValue.flightShipment.estimatedPickupDate"
                    v-model:validation-fail="validationViewModel[props.validationViewModelSection].estimatedPickupDate"
                    :requiredField="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'estimatedPickupDate' )"
                    :highlightRequiredBackground="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'estimatedPickupDate' )"
                    :labelWidth="70"
                    label="Pickup">
                  <el-date-picker
                      :clearable="false"
                      v-model="props.modelValue.flightShipment.estimatedPickupDate"
                      value-format="DD-MMM-YY"/>
                </DateInput>
                <MultiSearchDropDown
                    style="margin-left: 10px;"
                    :width="60"
                    placeholder="Time"
                    :highlightRequiredBackground="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'estimatedPickupTime')"
                    v-model:validation-fail="validationViewModel[props.validationViewModelSection].estimatedPickupTime"
                    v-model="props.modelValue.flightShipment.estimatedPickupTime"
                    :selectPrimitiveValue="true"
                    :disableFilter="true"
                    :data-source="useTimeGenerator(24)">
                </MultiSearchDropDown>
                <DateInput
                    style="margin-left: 10px;"
                    v-model="props.modelValue.flightShipment.actualPickupDate">
                  <el-date-picker
                      :clearable="false"
                      v-model="props.modelValue.flightShipment.actualPickupDate"
                      value-format="DD-MMM-YY"/>
                </DateInput>
                <MultiSearchDropDown
                    style="margin-left: 10px;"
                    :width="60"
                    :labelTop="true"
                    placeholder="Time"
                    v-model="props.modelValue.flightShipment.actualPickupTime"
                    :selectPrimitiveValue="true"
                    :disableFilter="true"
                    :data-source="useTimeGenerator(24)">
                </MultiSearchDropDown>
              </div>
              <div class="global-flex-layout" style="margin-top: 5px;">
                <DateInput
                    v-model="props.modelValue.flightShipment.estimatedDepartureDate"
                    v-model:validation-fail="validationViewModel[props.validationViewModelSection].estimatedDepartureDate"
                    :requiredField="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'estimatedDepartureDate' )"
                    :highlightRequiredBackground="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'estimatedDepartureDate')"
                    :labelWidth="70"
                    label="Departure">
                  <el-date-picker
                      :clearable="false"
                      v-model="props.modelValue.flightShipment.estimatedDepartureDate"
                      value-format="DD-MMM-YY"/>
                </DateInput>
                <MultiSearchDropDown
                    style="margin-left: 10px;"
                    :width="60"
                    placeholder="Time"
                    :requiredField="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'estimatedDepartureTime' )"
                    :highlightRequiredBackground="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'estimatedDepartureTime')"
                    v-model:validation-fail="validationViewModel[props.validationViewModelSection].estimatedDepartureTime"
                    v-model="props.modelValue.flightShipment.estimatedDepartureTime"
                    :selectPrimitiveValue="true"
                    :disableFilter="true"
                    :data-source="useTimeGenerator(24)">
                </MultiSearchDropDown>
                <DateInput
                    style="margin-left: 10px;"
                    v-model="props.modelValue.flightShipment.actualDepartureDate">
                  <el-date-picker
                      :clearable="false"
                      v-model="props.modelValue.flightShipment.actualDepartureDate"
                      value-format="DD-MMM-YY"/>
                </DateInput>
                <MultiSearchDropDown
                    style="margin-left: 10px;"
                    :width="60"
                    :labelTop="true"
                    placeholder="Time"
                    v-model="props.modelValue.flightShipment.actualDepartureTime"
                    :selectPrimitiveValue="true"
                    :disableFilter="true"
                    :data-source="useTimeGenerator(24)">
                </MultiSearchDropDown>
              </div>
              <div class="global-flex-layout" style="margin-top: 5px;">
                <DateInput
                    v-model="props.modelValue.flightShipment.estimatedArrivalDate"
                    v-model:validation-fail="validationViewModel[props.validationViewModelSection].estimatedArrivalDate"
                    :requiredField="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'estimatedArrivalDate' )"
                    :highlightRequiredBackground="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'estimatedArrivalDate')"
                    :labelWidth="70"
                    label="Arrival">
                  <el-date-picker
                      :clearable="false"
                      v-model="props.modelValue.flightShipment.estimatedArrivalDate"
                      value-format="DD-MMM-YY"/>
                </DateInput>
                <MultiSearchDropDown
                    style="margin-left: 10px;"
                    :width="60"
                    placeholder="Time"
                    :requiredField="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'estimatedArrivalTime' )"
                    :highlightRequiredBackground="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'estimatedArrivalTime')"
                    v-model:validation-fail="validationViewModel[props.validationViewModelSection].estimatedArrivalTime"
                    v-model="props.modelValue.flightShipment.estimatedArrivalTime"
                    :selectPrimitiveValue="true"
                    :disableFilter="true"
                    :data-source="useTimeGenerator(24)">
                </MultiSearchDropDown>
                <DateInput
                    style="margin-left: 10px;"
                    v-model="props.modelValue.flightShipment.actualArrivalDate">
                  <el-date-picker
                      :clearable="false"
                      v-model="props.modelValue.flightShipment.actualArrivalDate"
                      value-format="DD-MMM-YY"/>
                </DateInput>
                <MultiSearchDropDown
                    style="margin-left: 10px;"
                    :width="60"
                    :labelTop="true"
                    placeholder="Time"
                    v-model="props.modelValue.flightShipment.actualArrivalTime"
                    :selectPrimitiveValue="true"
                    :disableFilter="true"
                    :data-source="useTimeGenerator(24)">
                </MultiSearchDropDown>
              </div>
              <div class="global-flex-layout" style="margin-top: 5px;">
                <DateInput
                    v-model="props.modelValue.flightShipment.estimatedDeliveryDate"
                    v-model:validation-fail="validationViewModel[props.validationViewModelSection].estimatedDeliveryDate"
                    :requiredField="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'estimatedDeliveryDate' )"
                    :highlightRequiredBackground="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'estimatedDeliveryDate')"
                    :labelWidth="70"
                    label="Delivery">
                  <el-date-picker
                      :clearable="false"
                      v-model="props.modelValue.flightShipment.estimatedDeliveryDate"
                      value-format="DD-MMM-YY"/>
                </DateInput>
                <MultiSearchDropDown
                    style="margin-left: 10px;"
                    :width="60"
                    placeholder="Time"
                    :requiredField="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'estimatedDeliveryTime' )"
                    :highlightRequiredBackground="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'estimatedDeliveryTime')"
                    v-model:validation-fail="validationViewModel[props.validationViewModelSection].estimatedDeliveryTime"
                    v-model="props.modelValue.flightShipment.estimatedDeliveryTime"
                    :selectPrimitiveValue="true"
                    :disableFilter="true"
                    :data-source="useTimeGenerator(24)">
                </MultiSearchDropDown>
                <DateInput
                    style="margin-left: 10px;"
                    v-model="props.modelValue.flightShipment.actualDeliveryDate">
                  <el-date-picker
                      :clearable="false"
                      v-model="props.modelValue.flightShipment.actualDeliveryDate"
                      value-format="DD-MMM-YY"/>
                </DateInput>
                <MultiSearchDropDown
                    style="margin-left: 10px;"
                    :width="60"
                    :labelTop="true"
                    placeholder="Time"
                    v-model="props.modelValue.flightShipment.actualDeliveryTime"
                    :selectPrimitiveValue="true"
                    :disableFilter="true"
                    :data-source="useTimeGenerator(24)">
                </MultiSearchDropDown>
              </div>
              <!--              <div class="cmp-multi-booking_drop-down-buttons" style="bottom: 3px;">-->
              <!--                <Button @click="handleTileSelection('flightShipment')" text="Close" type="toolbar"></Button>-->
              <!--                <Button @click="handleValidateAndClose('flightShipment')" text="OK"></Button>-->
              <!--              </div>-->
            </div>
          </div>
        </div>
        <div v-if="props.modelValue.tileSelection.seaShipment" class="cmp-multi-booking_drop-down">
          <div class="cmp-multi-booking_input-container cmp-multi-booking_add-border">
            <div class="global-css_vertical-form-layout">
              <MultiSearchDropDown
                  :width="300"
                  :labelWidth="110"
                  :transport-mode="route.params.section"
                  :requiredField="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'placeOfReceipt' )"
                  :allowCustomValue="false"
                  :selectPrimitiveValue="true"
                  :useServerSearch="true"
                  @select="setPortId('seaShipment','placeOfReceiptId', $event.id)"
                  searchType="unlocode"
                  placeholder="Search UNLOCODE...."
                  v-model:validation-fail="validationViewModel[props.validationViewModelSection].placeOfReceipt"
                  label="Place Of Receipt"
                  v-model="props.modelValue.seaShipment.placeOfReceipt">
              </MultiSearchDropDown>
              <MultiSearchDropDown
                  :width="300"
                  :labelWidth="110"
                  :transport-mode="route.params.section"
                  :requiredField="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'originPort' )"
                  :allowCustomValue="false"
                  :selectPrimitiveValue="true"
                  :useServerSearch="true"
                  @select="setPortId('seaShipment','originPortId', $event.id)"
                  searchType="unlocode"
                  placeholder="Search UNLOCODE...."
                  v-model:validation-fail="validationViewModel[props.validationViewModelSection].originPort"
                  label="Origin Port"
                  v-model="props.modelValue.seaShipment.originPort">
              </MultiSearchDropDown>
              <MultiSearchDropDown
                  :width="300"
                  :labelWidth="110"
                  :transport-mode="route.params.section"
                  :requiredField="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'destinationPort' )"
                  :allowCustomValue="false"
                  :selectPrimitiveValue="true"
                  :useServerSearch="true"
                  @select="setPortId('seaShipment','destinationPortId', $event.id)"
                  searchType="unlocode"
                  placeholder="Search UNLOCODE...."
                  v-model:validation-fail="validationViewModel[props.validationViewModelSection].destinationPort"
                  label="Destination Port"
                  v-model="props.modelValue.seaShipment.destinationPort">
              </MultiSearchDropDown>
              <MultiSearchDropDown
                  :width="300"
                  :labelWidth="110"
                  :transport-mode="route.params.section"
                  :requiredField="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'finalDestination' )"
                  :allowCustomValue="false"
                  :selectPrimitiveValue="true"
                  :useServerSearch="true"
                  @select="setPortId('seaShipment','finalDestinationId', $event.id)"
                  searchType="unlocode"
                  placeholder="Search UNLOCODE...."
                  v-model:validation-fail="validationViewModel[props.validationViewModelSection].finalDestination"
                  label="Final Destination"
                  v-model="props.modelValue.seaShipment.finalDestination">
              </MultiSearchDropDown>
              <TextInput
                  :width="300"
                  :labelWidth="110"
                  v-model="props.modelValue.seaShipment.bolNumber"
                  v-model:validation-fail="validationViewModel[props.validationViewModelSection].bolNumber"
                  :requiredField="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'bolNumber' )"
                  label="Bill Of Lading No.">
              </TextInput>
              <TextInput
                  :width="300"
                  :labelWidth="110"
                  v-model="props.modelValue.seaShipment.carrierName"
                  v-model:validation-fail="validationViewModel[props.validationViewModelSection].carrierName"
                  :requiredField="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'carrierName' )"
                  label="Carrier Name">
              </TextInput>
              <TextInput
                  :width="300"
                  :labelWidth="110"
                  v-model="props.modelValue.seaShipment.vesselName"
                  v-model:validation-fail="validationViewModel[props.validationViewModelSection].vesselName"
                  :requiredField="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'vesselName' )"
                  label="Vessel Name">
              </TextInput>
              <TextInput
                  :width="300"
                  :labelWidth="110"
                  v-model="props.modelValue.seaShipment.voyageNumber"
                  v-model:validation-fail="validationViewModel[props.validationViewModelSection].voyageNumber"
                  :requiredField="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'voyageNumber' )"
                  label="Voyage Number">
              </TextInput>
            </div>
            <div class="global-css_vertical-form-layout">
              <TextInput
                  :width="380"
                  :labelWidth="120"
                  v-model="props.modelValue.seaShipment.customerRef"
                  v-model:validation-fail="validationViewModel[props.validationViewModelSection].customerRef"
                  :requiredField="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'customerRef' )"
                  label="Customer Ref">
              </TextInput>
              <MultiSearchDropDown
                  :width="380"
                  :labelWidth="120"
                  :requiredField="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'terms' )"
                  :allowCustomValue="false"
                  :selectPrimitiveValue="true"
                  placeholder="Search or Select...."
                  v-model:validation-fail="validationViewModel[props.validationViewModelSection].terms"
                  label="Terms"
                  v-model="props.modelValue.seaShipment.terms"
                  :data-source="props.modelValue.metaData.terms">
              </MultiSearchDropDown>
              <MultiSearchDropDown
                  :width="380"
                  :labelWidth="120"
                  :requiredField="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'serviceType' )"
                  :allowCustomValue="false"
                  :selectPrimitiveValue="true"
                  placeholder="Search or Select...."
                  v-model:validation-fail="validationViewModel[props.validationViewModelSection].serviceType"
                  label="Service Type"
                  v-model="props.modelValue.seaShipment.serviceType"
                  :data-source="props.modelValue.metaData.serviceTypes">
              </MultiSearchDropDown>
              <TextInput
                  :width="380"
                  :labelWidth="120"
                  v-model="props.modelValue.seaShipment.notifyParty"
                  v-model:validation-fail="validationViewModel[props.validationViewModelSection].notifyParty"
                  :requiredField="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'notifyParty' )"
                  label="Notify Party">
              </TextInput>
              <DropDownTextArea
                  :width="380"
                  :labelWidth="120"
                  :requiredField="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'notifyPartyAddress' )"
                  v-model:validation-fail="validationViewModel[props.validationViewModelSection].notifyPartyAddress"
                  v-model="props.modelValue.seaShipment.notifyPartyAddress"
                  label="Notify Address">
              </DropDownTextArea>
              <div class="global-flex-layout">
                <TextInput
                    :width="75"
                    :labelWidth="120"
                    v-model="props.modelValue.seaShipment.shipmentValue"
                    v-model:validation-fail="validationViewModel[props.validationViewModelSection].shipmentValue"
                    :requiredField="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'shipmentValue' )"
                    label="Value">
                </TextInput>
                <MultiSearchDropDown
                    :width="220"
                    :labelWidth="70"
                    :requiredField="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'currency' )"
                    :allowCustomValue="false"
                    :selectPrimitiveValue="true"
                    placeholder="Search or Select...."
                    v-model:validation-fail="validationViewModel[props.validationViewModelSection].currency"
                    label="Currency"
                    v-model="props.modelValue.seaShipment.currency"
                    :data-source="props.modelValue.metaData.currencies">
                </MultiSearchDropDown>
              </div>
              <DropDownTextArea
                  :width="380"
                  :labelWidth="120"
                  :maxLength="42"
                  :requiredField="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'marksNumbers' )"
                  v-model:validation-fail="validationViewModel[props.validationViewModelSection].marksNumbers"
                  v-model="props.modelValue.seaShipment.marksNumbers"
                  label="Marks & Numbers">
              </DropDownTextArea>
              <div class="global-flex-layout">
                <DropDownTextArea
                    :width="123"
                    :textAreaWidth="380"
                    :labelWidth="120"
                    :maxLength="10"
                    :requiredField="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'commodity' )"
                    v-model:validation-fail="validationViewModel[props.validationViewModelSection].commodity"
                    v-model="props.modelValue.seaShipment.commodity"
                    label="Commodity">
                </DropDownTextArea>
                <DropDownTextArea
                    :width="118"
                    :textAreaWidth="300"
                    :labelWidth="125"
                    :maxLength="10"
                    :requiredField="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'remarks' )"
                    v-model:validation-fail="validationViewModel[props.validationViewModelSection].remarks"
                    v-model="props.modelValue.seaShipment.remarks"
                    label="Additional Remarks">
                </DropDownTextArea>
              </div>
            </div>
            <div class="cmp-multi-booking_date-areas" style="width: 620px;">
              <Tabs v-model:selectedTab="props.modelValue.tabSelect">
                <TabsContent
                    style="margin-left: 30px"
                    icon="fa-regular fa-calendar-days"
                    name="Dates"
                    :selectedTab="props.modelValue.tabSelect">
                  <div class="global-flex-layout cmp-multi-booking_date-area-headings"
                       style="margin-bottom: -10px;">
                    <div>Estimated Dates</div>
                    <div>Actual Dates</div>
                  </div>
                  <div class="global-flex-layout" style="margin-top: 35px;">
                    <DateInput
                        v-model="props.modelValue.seaShipment.estimatedPickupDate"
                        v-model:validation-fail="validationViewModel[props.validationViewModelSection].estimatedPickupDate"
                        :requiredField="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'estimatedPickupDate' )"
                        :highlightRequiredBackground="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'estimatedPickupDate')"
                        :labelWidth="70"
                        label="Pickup">
                      <el-date-picker
                          :clearable="false"
                          v-model="props.modelValue.seaShipment.estimatedPickupDate"
                          value-format="DD-MMM-YY"/>
                    </DateInput>
                    <MultiSearchDropDown
                        style="margin-left: 10px;"
                        :width="60"
                        :requiredField="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'estimatedPickupTime' )"
                        :highlightRequiredBackground="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'estimatedPickupTime')"
                        placeholder="Time"
                        v-model:validation-fail="validationViewModel[props.validationViewModelSection].estimatedPickupTime"
                        v-model="props.modelValue.seaShipment.estimatedPickupTime"
                        :selectPrimitiveValue="true"
                        :disableFilter="true"
                        :data-source="useTimeGenerator(24)">
                    </MultiSearchDropDown>
                    <DateInput
                        style="margin-left: 10px;"
                        v-model="props.modelValue.seaShipment.actualPickupDate">
                      <el-date-picker
                          :clearable="false"
                          v-model="props.modelValue.seaShipment.actualPickupDate"
                          value-format="DD-MMM-YY"/>
                    </DateInput>
                    <MultiSearchDropDown
                        style="margin-left: 10px;"
                        :width="60"
                        :labelTop="true"
                        placeholder="Time"
                        v-model="props.modelValue.seaShipment.actualPickupTime"
                        :selectPrimitiveValue="true"
                        :disableFilter="true"
                        :data-source="useTimeGenerator(24)">
                    </MultiSearchDropDown>
                  </div>
                  <div class="global-flex-layout" style="margin-top: 5px;">
                    <DateInput
                        v-model="props.modelValue.seaShipment.estimatedDepartureDate"
                        v-model:validation-fail="validationViewModel[props.validationViewModelSection].estimatedDepartureDate"
                        :requiredField="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'estimatedDepartureDate' )"
                        :highlightRequiredBackground="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'estimatedDepartureDate')"
                        :labelWidth="70"
                        label="Departure">
                      <el-date-picker
                          :clearable="false"
                          v-model="props.modelValue.seaShipment.estimatedDepartureDate"
                          value-format="DD-MMM-YY"/>
                    </DateInput>
                    <MultiSearchDropDown
                        style="margin-left: 10px;"
                        :width="60"
                        :requiredField="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'estimatedDepartureTime' )"
                        :highlightRequiredBackground="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'estimatedDepartureTime')"
                        placeholder="Time"
                        v-model:validation-fail="validationViewModel[props.validationViewModelSection].estimatedDepartureTime"
                        v-model="props.modelValue.seaShipment.estimatedDepartureTime"
                        :selectPrimitiveValue="true"
                        :disableFilter="true"
                        :data-source="useTimeGenerator(24)">
                    </MultiSearchDropDown>
                    <DateInput
                        style="margin-left: 10px;"
                        v-model="props.modelValue.seaShipment.actualDepartureDate">
                      <el-date-picker
                          :clearable="false"
                          v-model="props.modelValue.seaShipment.actualDepartureDate"
                          value-format="DD-MMM-YY"/>
                    </DateInput>
                    <MultiSearchDropDown
                        style="margin-left: 10px;"
                        :width="60"
                        :labelTop="true"
                        placeholder="Time"
                        v-model="props.modelValue.seaShipment.actualDepartureTime"
                        :selectPrimitiveValue="true"
                        :disableFilter="true"
                        :data-source="useTimeGenerator(24)">
                    </MultiSearchDropDown>
                  </div>
                  <div class="global-flex-layout" style="margin-top: 5px;">
                    <DateInput
                        v-model="props.modelValue.seaShipment.estimatedArrivalDate"
                        v-model:validation-fail="validationViewModel[props.validationViewModelSection].estimatedArrivalDate"
                        :requiredField="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'estimatedArrivalDate' )"
                        :highlightRequiredBackground="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'estimatedArrivalDate')"
                        :labelWidth="70"
                        label="Arrival">
                      <el-date-picker
                          :clearable="false"
                          v-model="props.modelValue.seaShipment.estimatedArrivalDate"
                          value-format="DD-MMM-YY"/>
                    </DateInput>
                    <MultiSearchDropDown
                        style="margin-left: 10px;"
                        :width="60"
                        :requiredField="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'estimatedArrivalTime' )"
                        :highlightRequiredBackground="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'estimatedArrivalTime')"
                        placeholder="Time"
                        v-model:validation-fail="validationViewModel[props.validationViewModelSection].estimatedArrivalTime"
                        v-model="props.modelValue.seaShipment.estimatedArrivalTime"
                        :selectPrimitiveValue="true"
                        :disableFilter="true"
                        :data-source="useTimeGenerator(24)">
                    </MultiSearchDropDown>
                    <DateInput
                        style="margin-left: 10px;"
                        v-model="props.modelValue.seaShipment.actualArrivalDate">
                      <el-date-picker
                          :clearable="false"
                          v-model="props.modelValue.seaShipment.actualArrivalDate"
                          value-format="DD-MMM-YY"/>
                    </DateInput>
                    <MultiSearchDropDown
                        style="margin-left: 10px;"
                        :width="60"
                        :labelTop="true"
                        placeholder="Time"
                        v-model="props.modelValue.seaShipment.actualArrivalTime"
                        :selectPrimitiveValue="true"
                        :disableFilter="true"
                        :data-source="useTimeGenerator(24)">
                    </MultiSearchDropDown>
                  </div>
                  <div class="global-flex-layout" style="height: 35px;">
                    <DateInput
                        v-model="props.modelValue.seaShipment.estimatedDeliveryDate"
                        v-model:validation-fail="validationViewModel[props.validationViewModelSection].estimatedDeliveryDate"
                        :requiredField="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'estimatedDeliveryDate' )"
                        :highlightRequiredBackground="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'estimatedDeliveryDate')"
                        :labelWidth="70"
                        label="Delivery">
                      <el-date-picker
                          :clearable="false"
                          v-model="props.modelValue.seaShipment.estimatedDeliveryDate"
                          value-format="DD-MMM-YY"/>
                    </DateInput>
                    <MultiSearchDropDown
                        style="margin-left: 10px;"
                        :width="60"
                        :requiredField="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'estimatedDeliveryTime' )"
                        :highlightRequiredBackground="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'estimatedDeliveryTime')"
                        placeholder="Time"
                        v-model:validation-fail="validationViewModel[props.validationViewModelSection].estimatedDeliveryTime"
                        v-model="props.modelValue.seaShipment.estimatedDeliveryTime"
                        :selectPrimitiveValue="true"
                        :disableFilter="true"
                        :data-source="useTimeGenerator(24)">
                    </MultiSearchDropDown>
                    <DateInput
                        style="margin-left: 10px;"
                        v-model="props.modelValue.seaShipment.actualDeliveryDate">
                      <el-date-picker
                          :clearable="false"
                          v-model="props.modelValue.seaShipment.actualDeliveryDate"
                          value-format="DD-MMM-YY"/>
                    </DateInput>
                    <MultiSearchDropDown
                        style="margin-left: 10px;"
                        :width="60"
                        :labelTop="true"
                        placeholder="Time"
                        v-model="props.modelValue.seaShipment.actualDeliveryTime"
                        :selectPrimitiveValue="true"
                        :disableFilter="true"
                        :data-source="useTimeGenerator(24)">
                    </MultiSearchDropDown>
                  </div>
                </TabsContent>
                <TabsContent
                    icon="fa-regular fa-truck-container"
                    name="Containers"
                    :selectedTab="props.modelValue.tabSelect">
                <span class="cmp-multi-booking_dims-message" style="margin-left: 0">
                Complete all fields to add a container
              </span>
                  <ContainerMultiEntry
                      style="margin-top: 10px;"
                      :containers="props.modelValue.metaData.containerTypes"
                      v-model="props.modelValue.seaShipment.containers">
                  </ContainerMultiEntry>
                </TabsContent>
              </Tabs>
              <!--              <div class="cmp-multi-booking_drop-down-buttons" style="bottom: 3px; right: 30px">-->
              <!--                <Button @click="handleTileSelection('seaShipment')" text="Close" type="toolbar"></Button>-->
              <!--                <Button @click="handleValidateAndClose('seaShipment')" text="OK"></Button>-->
              <!--              </div>-->
            </div>
          </div>
        </div>
        <div v-if="props.modelValue.tileSelection.roadShipment" class="cmp-multi-booking_drop-down">
          <div class="cmp-multi-booking_input-container cmp-multi-booking_add-border">
            <i class="fa-thin fa-truck cmp-multi-booking_tile-icon"></i>
            <div class="global-css_vertical-form-layout" style="padding-top: 20px">
              <MultiSearchDropDown
                  :width="300"
                  :labelWidth="110"
                  :transport-mode="route.params.section"
                  :requiredField="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'placeOfReceipt' )"
                  :allowCustomValue="false"
                  :selectPrimitiveValue="true"
                  :useServerSearch="true"
                  @select="setPortId('roadShipment','placeOfReceiptId', $event.id)"
                  searchType="unlocode"
                  placeholder="Search UNLOCODE...."
                  v-model:validation-fail="validationViewModel[props.validationViewModelSection].placeOfReceipt"
                  label="Place Of Receipt"
                  v-model="props.modelValue.roadShipment.placeOfReceipt">
              </MultiSearchDropDown>
              <MultiSearchDropDown
                  :width="300"
                  :labelWidth="110"
                  :transport-mode="route.params.section"
                  :requiredField="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'originPort' )"
                  :allowCustomValue="false"
                  :selectPrimitiveValue="true"
                  :useServerSearch="true"
                  @select="setPortId('roadShipment','originPortId', $event.id)"
                  searchType="iata"
                  placeholder="Search IATA code...."
                  v-model:validation-fail="validationViewModel[props.validationViewModelSection].originPort"
                  label="Origin Port"
                  v-model="props.modelValue.roadShipment.originPort">
              </MultiSearchDropDown>
              <MultiSearchDropDown
                  :width="300"
                  :labelWidth="110"
                  :transport-mode="route.params.section"
                  :requiredField="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'destinationPort' )"
                  :allowCustomValue="false"
                  :selectPrimitiveValue="true"
                  :useServerSearch="true"
                  @select="setPortId('roadShipment','destinationPortId', $event.id)"
                  searchType="iata"
                  placeholder="Search IATA code...."
                  v-model:validation-fail="validationViewModel[props.validationViewModelSection].destinationPort"
                  label="Destination Port"
                  v-model="props.modelValue.roadShipment.destinationPort">
              </MultiSearchDropDown>
              <MultiSearchDropDown
                  :width="300"
                  :labelWidth="110"
                  :transport-mode="route.params.section"
                  :requiredField="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'finalDestination' )"
                  :allowCustomValue="false"
                  :selectPrimitiveValue="true"
                  :useServerSearch="true"
                  @select="setPortId('roadShipment','finalDestinationId', $event.id)"
                  searchType="unlocode"
                  placeholder="Search UNLOCODE...."
                  v-model:validation-fail="validationViewModel[props.validationViewModelSection].finalDestination"
                  label="Final Destination"
                  v-model="props.modelValue.roadShipment.finalDestination">
              </MultiSearchDropDown>
              <TextInput
                  :width="300"
                  :labelWidth="110"
                  v-model="props.modelValue.roadShipment.customerRef"
                  v-model:validation-fail="validationViewModel[props.validationViewModelSection].customerRef"
                  :requiredField="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'customerRef' )"
                  label="Customer Ref">
              </TextInput>
              <TextInput
                  :width="300"
                  :labelWidth="110"
                  v-model="props.modelValue.roadShipment.sealNumber"
                  v-model:validation-fail="validationViewModel[props.validationViewModelSection].sealNumber"
                  :requiredField="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'sealNumber' )"
                  label="Seal Number">
              </TextInput>
            </div>
            <div class="global-css_vertical-form-layout" style="padding-top: 20px">
              <MultiSearchDropDown
                  :width="380"
                  :labelWidth="128"
                  :requiredField="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'terms' )"
                  :allowCustomValue="false"
                  :selectPrimitiveValue="true"
                  placeholder="Search or Select...."
                  v-model:validation-fail="validationViewModel[props.validationViewModelSection].terms"
                  label="Terms"
                  v-model="props.modelValue.roadShipment.terms"
                  :data-source="props.modelValue.metaData.terms">
              </MultiSearchDropDown>
              <MultiSearchDropDown
                  :width="380"
                  :labelWidth="128"
                  :requiredField="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'serviceType' )"
                  :allowCustomValue="false"
                  :selectPrimitiveValue="true"
                  placeholder="Search or Select...."
                  v-model:validation-fail="validationViewModel[props.validationViewModelSection].serviceType"
                  label="Service Type"
                  v-model="props.modelValue.roadShipment.serviceType"
                  :data-source="props.modelValue.metaData.serviceTypes">
              </MultiSearchDropDown>
              <div class="global-flex-layout">
                <TextInput
                    :width="75"
                    :labelWidth="128"
                    v-model="props.modelValue.roadShipment.shipmentValue"
                    v-model:validation-fail="validationViewModel[props.validationViewModelSection].shipmentValue"
                    :requiredField="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'shipmentValue' )"
                    label="Value">
                </TextInput>
                <MultiSearchDropDown
                    :width="228"
                    :labelWidth="62"
                    :requiredField="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'currency' )"
                    :allowCustomValue="false"
                    :selectPrimitiveValue="true"
                    placeholder="Search or Select...."
                    v-model:validation-fail="validationViewModel[props.validationViewModelSection].currency"
                    label="Currency"
                    v-model="props.modelValue.roadShipment.currency"
                    :data-source="props.modelValue.metaData.currencies">
                </MultiSearchDropDown>
              </div>
              <DropDownTextArea
                  :width="380"
                  :labelWidth="128"
                  :maxLength="42"
                  :requiredField="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'commodity' )"
                  v-model:validation-fail="validationViewModel[props.validationViewModelSection].commodity"
                  v-model="props.modelValue.roadShipment.commodity"
                  label="Commodity">
              </DropDownTextArea>
              <DropDownTextArea
                  :width="380"
                  :labelWidth="128"
                  :maxLength="42"
                  :requiredField="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'remarks' )"
                  v-model:validation-fail="validationViewModel[props.validationViewModelSection].remarks"
                  v-model="props.modelValue.roadShipment.remarks"
                  label="Additional Remarks">
              </DropDownTextArea>
            </div>
            <div class="cmp-multi-booking_date-area">
              <div class="global-flex-layout cmp-multi-booking_date-area-headings">
                <div>Estimated Dates</div>
                <div>Actual Dates</div>
              </div>
              <div class="global-flex-layout" style="margin-top: 35px;">
                <DateInput
                    v-model="props.modelValue.roadShipment.estimatedPickupDate"
                    v-model:validation-fail="validationViewModel[props.validationViewModelSection].estimatedPickupDate"
                    :requiredField="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'estimatedPickupDate' )"
                    :highlightRequiredBackground="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'estimatedPickupDate' )"
                    :labelWidth="70"
                    label="Pickup">
                  <el-date-picker
                      :clearable="false"
                      v-model="props.modelValue.roadShipment.estimatedPickupDate"
                      value-format="DD-MMM-YY"/>
                </DateInput>
                <MultiSearchDropDown
                    style="margin-left: 10px;"
                    :width="60"
                    placeholder="Time"
                    :highlightRequiredBackground="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'estimatedPickupTime')"
                    v-model:validation-fail="validationViewModel[props.validationViewModelSection].estimatedPickupTime"
                    v-model="props.modelValue.roadShipment.estimatedPickupTime"
                    :selectPrimitiveValue="true"
                    :disableFilter="true"
                    :data-source="useTimeGenerator(24)">
                </MultiSearchDropDown>
                <DateInput
                    style="margin-left: 10px;"
                    v-model="props.modelValue.roadShipment.actualPickupDate">
                  <el-date-picker
                      :clearable="false"
                      v-model="props.modelValue.roadShipment.actualPickupDate"
                      value-format="DD-MMM-YY"/>
                </DateInput>
                <MultiSearchDropDown
                    style="margin-left: 10px;"
                    :width="60"
                    :labelTop="true"
                    placeholder="Time"
                    v-model="props.modelValue.roadShipment.actualPickupTime"
                    :selectPrimitiveValue="true"
                    :disableFilter="true"
                    :data-source="useTimeGenerator(24)">
                </MultiSearchDropDown>
              </div>
              <div class="global-flex-layout" style="margin-top: 5px;">
                <DateInput
                    v-model="props.modelValue.roadShipment.estimatedDepartureDate"
                    v-model:validation-fail="validationViewModel[props.validationViewModelSection].estimatedDepartureDate"
                    :requiredField="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'estimatedDepartureDate' )"
                    :highlightRequiredBackground="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'estimatedDepartureDate')"
                    :labelWidth="70"
                    label="Departure">
                  <el-date-picker
                      :clearable="false"
                      v-model="props.modelValue.roadShipment.estimatedDepartureDate"
                      value-format="DD-MMM-YY"/>
                </DateInput>
                <MultiSearchDropDown
                    style="margin-left: 10px;"
                    :width="60"
                    placeholder="Time"
                    :requiredField="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'estimatedDepartureTime' )"
                    :highlightRequiredBackground="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'estimatedDepartureTime')"
                    v-model:validation-fail="validationViewModel[props.validationViewModelSection].estimatedDepartureTime"
                    v-model="props.modelValue.roadShipment.estimatedDepartureTime"
                    :selectPrimitiveValue="true"
                    :disableFilter="true"
                    :data-source="useTimeGenerator(24)">
                </MultiSearchDropDown>
                <DateInput
                    style="margin-left: 10px;"
                    v-model="props.modelValue.roadShipment.actualDepartureDate">
                  <el-date-picker
                      :clearable="false"
                      v-model="props.modelValue.roadShipment.actualDepartureDate"
                      value-format="DD-MMM-YY"/>
                </DateInput>
                <MultiSearchDropDown
                    style="margin-left: 10px;"
                    :width="60"
                    :labelTop="true"
                    placeholder="Time"
                    v-model="props.modelValue.roadShipment.actualDepartureTime"
                    :selectPrimitiveValue="true"
                    :disableFilter="true"
                    :data-source="useTimeGenerator(24)">
                </MultiSearchDropDown>
              </div>
              <div class="global-flex-layout" style="margin-top: 5px;">
                <DateInput
                    v-model="props.modelValue.roadShipment.estimatedArrivalDate"
                    v-model:validation-fail="validationViewModel[props.validationViewModelSection].estimatedArrivalDate"
                    :requiredField="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'estimatedArrivalDate' )"
                    :highlightRequiredBackground="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'estimatedArrivalDate')"
                    :labelWidth="70"
                    label="Arrival">
                  <el-date-picker
                      :clearable="false"
                      v-model="props.modelValue.roadShipment.estimatedArrivalDate"
                      value-format="DD-MMM-YY"/>
                </DateInput>
                <MultiSearchDropDown
                    style="margin-left: 10px;"
                    :width="60"
                    placeholder="Time"
                    :requiredField="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'estimatedArrivalTime' )"
                    :highlightRequiredBackground="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'estimatedArrivalTime')"
                    v-model:validation-fail="validationViewModel[props.validationViewModelSection].estimatedArrivalTime"
                    v-model="props.modelValue.roadShipment.estimatedArrivalTime"
                    :selectPrimitiveValue="true"
                    :disableFilter="true"
                    :data-source="useTimeGenerator(24)">
                </MultiSearchDropDown>
                <DateInput
                    style="margin-left: 10px;"
                    v-model="props.modelValue.roadShipment.actualArrivalDate">
                  <el-date-picker
                      :clearable="false"
                      v-model="props.modelValue.roadShipment.actualArrivalDate"
                      value-format="DD-MMM-YY"/>
                </DateInput>
                <MultiSearchDropDown
                    style="margin-left: 10px;"
                    :width="60"
                    :labelTop="true"
                    placeholder="Time"
                    v-model="props.modelValue.roadShipment.actualArrivalTime"
                    :selectPrimitiveValue="true"
                    :disableFilter="true"
                    :data-source="useTimeGenerator(24)">
                </MultiSearchDropDown>
              </div>
              <div class="global-flex-layout" style="margin-top: 5px;">
                <DateInput
                    v-model="props.modelValue.roadShipment.estimatedDeliveryDate"
                    v-model:validation-fail="validationViewModel[props.validationViewModelSection].estimatedDeliveryDate"
                    :requiredField="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'estimatedDeliveryDate' )"
                    :highlightRequiredBackground="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'estimatedDeliveryDate')"
                    :labelWidth="70"
                    label="Delivery">
                  <el-date-picker
                      :clearable="false"
                      v-model="props.modelValue.roadShipment.estimatedDeliveryDate"
                      value-format="DD-MMM-YY"/>
                </DateInput>
                <MultiSearchDropDown
                    style="margin-left: 10px;"
                    :width="60"
                    placeholder="Time"
                    :requiredField="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'estimatedDeliveryTime' )"
                    :highlightRequiredBackground="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'estimatedDeliveryTime')"
                    v-model:validation-fail="validationViewModel[props.validationViewModelSection].estimatedDeliveryTime"
                    v-model="props.modelValue.roadShipment.estimatedDeliveryTime"
                    :selectPrimitiveValue="true"
                    :disableFilter="true"
                    :data-source="useTimeGenerator(24)">
                </MultiSearchDropDown>
                <DateInput
                    style="margin-left: 10px;"
                    v-model="props.modelValue.roadShipment.actualDeliveryDate">
                  <el-date-picker
                      :clearable="false"
                      v-model="props.modelValue.roadShipment.actualDeliveryDate"
                      value-format="DD-MMM-YY"/>
                </DateInput>
                <MultiSearchDropDown
                    style="margin-left: 10px;"
                    :width="60"
                    :labelTop="true"
                    placeholder="Time"
                    v-model="props.modelValue.roadShipment.actualDeliveryTime"
                    :selectPrimitiveValue="true"
                    :disableFilter="true"
                    :data-source="useTimeGenerator(24)">
                </MultiSearchDropDown>
              </div>
              <!--              <div class="cmp-multi-booking_drop-down-buttons" style="bottom: 3px;">-->
              <!--                <Button @click="handleTileSelection('roadShipment')" text="Close" type="toolbar"></Button>-->
              <!--                <Button @click="handleValidateAndClose('roadShipment')" text="OK"></Button>-->
              <!--              </div>-->
            </div>
          </div>
        </div>
        <div v-if="props.modelValue.tileSelection.railShipment" class="cmp-multi-booking_drop-down">
          <div class="cmp-multi-booking_input-container cmp-multi-booking_add-border">
            <i class="fa-thin fa-train cmp-multi-booking_tile-icon"></i>
            <div class="global-css_vertical-form-layout" style="padding-top: 20px">
              <MultiSearchDropDown
                  :width="300"
                  :labelWidth="110"
                  :transport-mode="route.params.section"
                  :requiredField="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'placeOfReceipt' )"
                  :allowCustomValue="false"
                  :selectPrimitiveValue="true"
                  :useServerSearch="true"
                  @select="setPortId('railShipment','placeOfReceiptId', $event.id)"
                  searchType="unlocode"
                  placeholder="Search UNLOCODE...."
                  v-model:validation-fail="validationViewModel[props.validationViewModelSection].placeOfReceipt"
                  label="Place Of Receipt"
                  v-model="props.modelValue.railShipment.placeOfReceipt">
              </MultiSearchDropDown>
              <MultiSearchDropDown
                  :width="300"
                  :labelWidth="110"
                  :transport-mode="route.params.section"
                  :requiredField="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'originPort' )"
                  :allowCustomValue="false"
                  :selectPrimitiveValue="true"
                  :useServerSearch="true"
                  @select="setPortId('railShipment','originPortId', $event.id)"
                  searchType="iata"
                  placeholder="Search IATA code...."
                  v-model:validation-fail="validationViewModel[props.validationViewModelSection].originPort"
                  label="Origin Port"
                  v-model="props.modelValue.railShipment.originPort">
              </MultiSearchDropDown>
              <MultiSearchDropDown
                  :width="300"
                  :labelWidth="110"
                  :transport-mode="route.params.section"
                  :requiredField="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'destinationPort' )"
                  :allowCustomValue="false"
                  :selectPrimitiveValue="true"
                  :useServerSearch="true"
                  @select="setPortId('railShipment','destinationPortId', $event.id)"
                  searchType="iata"
                  placeholder="Search IATA code...."
                  v-model:validation-fail="validationViewModel[props.validationViewModelSection].destinationPort"
                  label="Destination Port"
                  v-model="props.modelValue.railShipment.destinationPort">
              </MultiSearchDropDown>
              <MultiSearchDropDown
                  :width="300"
                  :labelWidth="110"
                  :transport-mode="route.params.section"
                  :requiredField="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'finalDestination' )"
                  :allowCustomValue="false"
                  :selectPrimitiveValue="true"
                  :useServerSearch="true"
                  @select="setPortId('railShipment','finalDestinationId', $event.id)"
                  searchType="unlocode"
                  placeholder="Search UNLOCODE...."
                  v-model:validation-fail="validationViewModel[props.validationViewModelSection].finalDestination"
                  label="Final Destination"
                  v-model="props.modelValue.railShipment.finalDestination">
              </MultiSearchDropDown>
              <TextInput
                  :width="300"
                  :labelWidth="110"
                  v-model="props.modelValue.railShipment.customerRef"
                  v-model:validation-fail="validationViewModel[props.validationViewModelSection].customerRef"
                  :requiredField="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'customerRef' )"
                  label="Customer Ref">
              </TextInput>
              <TextInput
                  :width="300"
                  :labelWidth="110"
                  v-model="props.modelValue.railShipment.sealNumber"
                  v-model:validation-fail="validationViewModel[props.validationViewModelSection].sealNumber"
                  :requiredField="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'sealNumber' )"
                  label="Seal Number">
              </TextInput>
            </div>
            <div class="global-css_vertical-form-layout" style="padding-top: 20px">
              <MultiSearchDropDown
                  :width="380"
                  :labelWidth="128"
                  :requiredField="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'terms' )"
                  :allowCustomValue="false"
                  :selectPrimitiveValue="true"
                  placeholder="Search or Select...."
                  v-model:validation-fail="validationViewModel[props.validationViewModelSection].terms"
                  label="Terms"
                  v-model="props.modelValue.railShipment.terms"
                  :data-source="props.modelValue.metaData.terms">
              </MultiSearchDropDown>
              <MultiSearchDropDown
                  :width="380"
                  :labelWidth="128"
                  :requiredField="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'serviceType' )"
                  :allowCustomValue="false"
                  :selectPrimitiveValue="true"
                  placeholder="Search or Select...."
                  v-model:validation-fail="validationViewModel[props.validationViewModelSection].serviceType"
                  label="Service Type"
                  v-model="props.modelValue.railShipment.serviceType"
                  :data-source="props.modelValue.metaData.serviceTypes">
              </MultiSearchDropDown>
              <div class="global-flex-layout">
                <TextInput
                    :width="75"
                    :labelWidth="128"
                    v-model="props.modelValue.railShipment.shipmentValue"
                    v-model:validation-fail="validationViewModel[props.validationViewModelSection].shipmentValue"
                    :requiredField="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'shipmentValue' )"
                    label="Value">
                </TextInput>
                <MultiSearchDropDown
                    :width="228"
                    :labelWidth="62"
                    :requiredField="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'currency' )"
                    :allowCustomValue="false"
                    :selectPrimitiveValue="true"
                    placeholder="Search or Select...."
                    v-model:validation-fail="validationViewModel[props.validationViewModelSection].currency"
                    label="Currency"
                    v-model="props.modelValue.railShipment.currency"
                    :data-source="props.modelValue.metaData.currencies">
                </MultiSearchDropDown>
              </div>
              <DropDownTextArea
                  :width="380"
                  :labelWidth="128"
                  :maxLength="42"
                  :requiredField="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'commodity' )"
                  v-model:validation-fail="validationViewModel[props.validationViewModelSection].commodity"
                  v-model="props.modelValue.railShipment.commodity"
                  label="Commodity">
              </DropDownTextArea>
              <DropDownTextArea
                  :width="380"
                  :labelWidth="128"
                  :maxLength="42"
                  :requiredField="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'remarks' )"
                  v-model:validation-fail="validationViewModel[props.validationViewModelSection].remarks"
                  v-model="props.modelValue.railShipment.remarks"
                  label="Additional Remarks">
              </DropDownTextArea>
            </div>
            <div class="cmp-multi-booking_date-area">
              <div class="global-flex-layout cmp-multi-booking_date-area-headings">
                <div>Estimated Dates</div>
                <div>Actual Dates</div>
              </div>
              <div class="global-flex-layout" style="margin-top: 35px;">
                <DateInput
                    v-model="props.modelValue.railShipment.estimatedPickupDate"
                    v-model:validation-fail="validationViewModel[props.validationViewModelSection].estimatedPickupDate"
                    :requiredField="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'estimatedPickupDate' )"
                    :highlightRequiredBackground="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'estimatedPickupDate' )"
                    :labelWidth="70"
                    label="Pickup">
                  <el-date-picker
                      :clearable="false"
                      v-model="props.modelValue.railShipment.estimatedPickupDate"
                      value-format="DD-MMM-YY"/>
                </DateInput>
                <MultiSearchDropDown
                    style="margin-left: 10px;"
                    :width="60"
                    placeholder="Time"
                    :highlightRequiredBackground="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'estimatedPickupTime')"
                    v-model:validation-fail="validationViewModel[props.validationViewModelSection].estimatedPickupTime"
                    v-model="props.modelValue.railShipment.estimatedPickupTime"
                    :selectPrimitiveValue="true"
                    :disableFilter="true"
                    :data-source="useTimeGenerator(24)">
                </MultiSearchDropDown>
                <DateInput
                    style="margin-left: 10px;"
                    v-model="props.modelValue.railShipment.actualPickupDate">
                  <el-date-picker
                      :clearable="false"
                      v-model="props.modelValue.railShipment.actualPickupDate"
                      value-format="DD-MMM-YY"/>
                </DateInput>
                <MultiSearchDropDown
                    style="margin-left: 10px;"
                    :width="60"
                    :labelTop="true"
                    placeholder="Time"
                    v-model="props.modelValue.railShipment.actualPickupTime"
                    :selectPrimitiveValue="true"
                    :disableFilter="true"
                    :data-source="useTimeGenerator(24)">
                </MultiSearchDropDown>
              </div>
              <div class="global-flex-layout" style="margin-top: 5px;">
                <DateInput
                    v-model="props.modelValue.railShipment.estimatedDepartureDate"
                    v-model:validation-fail="validationViewModel[props.validationViewModelSection].estimatedDepartureDate"
                    :requiredField="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'estimatedDepartureDate' )"
                    :highlightRequiredBackground="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'estimatedDepartureDate')"
                    :labelWidth="70"
                    label="Departure">
                  <el-date-picker
                      :clearable="false"
                      v-model="props.modelValue.railShipment.estimatedDepartureDate"
                      value-format="DD-MMM-YY"/>
                </DateInput>
                <MultiSearchDropDown
                    style="margin-left: 10px;"
                    :width="60"
                    placeholder="Time"
                    :requiredField="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'estimatedDepartureTime' )"
                    :highlightRequiredBackground="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'estimatedDepartureTime')"
                    v-model:validation-fail="validationViewModel[props.validationViewModelSection].estimatedDepartureTime"
                    v-model="props.modelValue.railShipment.estimatedDepartureTime"
                    :selectPrimitiveValue="true"
                    :disableFilter="true"
                    :data-source="useTimeGenerator(24)">
                </MultiSearchDropDown>
                <DateInput
                    style="margin-left: 10px;"
                    v-model="props.modelValue.railShipment.actualDepartureDate">
                  <el-date-picker
                      :clearable="false"
                      v-model="props.modelValue.railShipment.actualDepartureDate"
                      value-format="DD-MMM-YY"/>
                </DateInput>
                <MultiSearchDropDown
                    style="margin-left: 10px;"
                    :width="60"
                    :labelTop="true"
                    placeholder="Time"
                    v-model="props.modelValue.railShipment.actualDepartureTime"
                    :selectPrimitiveValue="true"
                    :disableFilter="true"
                    :data-source="useTimeGenerator(24)">
                </MultiSearchDropDown>
              </div>
              <div class="global-flex-layout" style="margin-top: 5px;">
                <DateInput
                    v-model="props.modelValue.railShipment.estimatedArrivalDate"
                    v-model:validation-fail="validationViewModel[props.validationViewModelSection].estimatedArrivalDate"
                    :requiredField="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'estimatedArrivalDate' )"
                    :highlightRequiredBackground="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'estimatedArrivalDate')"
                    :labelWidth="70"
                    label="Arrival">
                  <el-date-picker
                      :clearable="false"
                      v-model="props.modelValue.railShipment.estimatedArrivalDate"
                      value-format="DD-MMM-YY"/>
                </DateInput>
                <MultiSearchDropDown
                    style="margin-left: 10px;"
                    :width="60"
                    placeholder="Time"
                    :requiredField="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'estimatedArrivalTime' )"
                    :highlightRequiredBackground="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'estimatedArrivalTime')"
                    v-model:validation-fail="validationViewModel[props.validationViewModelSection].estimatedArrivalTime"
                    v-model="props.modelValue.railShipment.estimatedArrivalTime"
                    :selectPrimitiveValue="true"
                    :disableFilter="true"
                    :data-source="useTimeGenerator(24)">
                </MultiSearchDropDown>
                <DateInput
                    style="margin-left: 10px;"
                    v-model="props.modelValue.railShipment.actualArrivalDate">
                  <el-date-picker
                      :clearable="false"
                      v-model="props.modelValue.railShipment.actualArrivalDate"
                      value-format="DD-MMM-YY"/>
                </DateInput>
                <MultiSearchDropDown
                    style="margin-left: 10px;"
                    :width="60"
                    :labelTop="true"
                    placeholder="Time"
                    v-model="props.modelValue.railShipment.actualArrivalTime"
                    :selectPrimitiveValue="true"
                    :disableFilter="true"
                    :data-source="useTimeGenerator(24)">
                </MultiSearchDropDown>
              </div>
              <div class="global-flex-layout" style="margin-top: 5px;">
                <DateInput
                    v-model="props.modelValue.railShipment.estimatedDeliveryDate"
                    v-model:validation-fail="validationViewModel[props.validationViewModelSection].estimatedDeliveryDate"
                    :requiredField="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'estimatedDeliveryDate' )"
                    :highlightRequiredBackground="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'estimatedDeliveryDate')"
                    :labelWidth="70"
                    label="Delivery">
                  <el-date-picker
                      :clearable="false"
                      v-model="props.modelValue.railShipment.estimatedDeliveryDate"
                      value-format="DD-MMM-YY"/>
                </DateInput>
                <MultiSearchDropDown
                    style="margin-left: 10px;"
                    :width="60"
                    placeholder="Time"
                    :requiredField="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'estimatedDeliveryTime' )"
                    :highlightRequiredBackground="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'estimatedDeliveryTime')"
                    v-model:validation-fail="validationViewModel[props.validationViewModelSection].estimatedDeliveryTime"
                    v-model="props.modelValue.railShipment.estimatedDeliveryTime"
                    :selectPrimitiveValue="true"
                    :disableFilter="true"
                    :data-source="useTimeGenerator(24)">
                </MultiSearchDropDown>
                <DateInput
                    style="margin-left: 10px;"
                    v-model="props.modelValue.railShipment.actualDeliveryDate">
                  <el-date-picker
                      :clearable="false"
                      v-model="props.modelValue.railShipment.actualDeliveryDate"
                      value-format="DD-MMM-YY"/>
                </DateInput>
                <MultiSearchDropDown
                    style="margin-left: 10px;"
                    :width="60"
                    :labelTop="true"
                    placeholder="Time"
                    v-model="props.modelValue.railShipment.actualDeliveryTime"
                    :selectPrimitiveValue="true"
                    :disableFilter="true"
                    :data-source="useTimeGenerator(24)">
                </MultiSearchDropDown>
              </div>
              <!--              <div class="cmp-multi-booking_drop-down-buttons" style="bottom: 3px;">-->
              <!--                <Button @click="handleTileSelection('roadShipment')" text="Close" type="toolbar"></Button>-->
              <!--                <Button @click="handleValidateAndClose('roadShipment')" text="OK"></Button>-->
              <!--              </div>-->
            </div>
          </div>
        </div>
        <div v-if="props.modelValue.tileSelection.dimensions" class="cmp-multi-booking_drop-down">
          <div class="cmp-multi-booking_input-container cmp-multi-booking_add-border">
            <div id="flex-area-one">
              <div style="display: flex; align-items: center; margin-bottom: 20px;">
                <i class="fa-thin fa-box-open cmp-multi-booking_tile-icon" style="margin-right: 20px;"></i>
                <div class="label" style="display: flex; align-items: center;">Packaging Details
                  <span class="cmp-multi-booking_dims-message">
                  Pieces, Length, Width & Height are required to add a line of dims
                </span>
                </div>
              </div>
              <DimensionsEntry
                  @entry-update="getChargeableWeightCalculation"
                  :packaging="props.modelValue.metaData.packagingTypes"
                  :validationFail="props.modelValue.tileValidation.dimensions"
                  :requiredFields="props.modelValue.metaData.validatedFields"
                  v-model="props.modelValue.dimensions.multiLineDims">
              </DimensionsEntry>
            </div>
            <div id="flex-area-two" style="margin-left: 100px;">
              <div style="display:flex; align-items: center; padding:5px;" class="label">Dims Totals
                <span class="cmp-multi-booking_dims-message">You may enter your own pieces and weight.</span>
              </div>
              <div class="cmp-multi-booking_dims-totals">
                <div class="label" style="margin-bottom: 14px; display: flex; align-items: center;">
                  <span style="font-weight: normal;">Unit Of Measure</span>
                  <span style="margin-left: 58px;">
                      <el-switch
                          v-model="props.modelValue.unitOfMeasureSwitch"
                          size="small"
                          active-text="lbs / inches"
                          inactive-text="kg / cms"
                          style="--el-switch-on-color:var(--aqua-blue-definition); --el-switch-off-color: var(--aqua-blue-definition)"/>
                  </span>
                </div>
                <div style="display:flex;">
                  <NumberOnlyInput
                      :width="80"
                      :label-width="50"
                      label="Pieces"
                      v-model="props.modelValue.dimensions.totalPieces"
                      v-model:validation-fail="validationViewModel[props.validationViewModelSection].totalPieces"
                      :requiredField="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'totalPieces' )">
                  </NumberOnlyInput>
                  <NumberOnlyInput
                      style="margin-left: 14px;"
                      :width="80"
                      :disable="allowWeightEntry"
                      :label-width="88"
                      label="Gross Weight"
                      v-model="props.modelValue.dimensions.totalWeight"
                      v-model:validation-fail="validationViewModel[props.validationViewModelSection].totalWeight"
                      :requiredField="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'totalWeight' )">
                  </NumberOnlyInput>
                  <TextInput
                      v-if="route.params.section === 'Road' || route.params.section === 'Rail'"
                      style="margin-left: 14px;"
                      :width="80"
                      :readonly="true"
                      :label-width="110"
                      v-model="props.modelValue.dimensions.chargeableWeight"
                      label="Chargeable Weight">
                  </TextInput>
                </div>
                <div v-if="route.params.section !== 'Air'" class="global-flex-layout" style="margin-top: 5px;">
                  <NumberOnlyInput
                      :width="80"
                      :allowDecimal="true"
                      :label-width="50"
                      v-model="props.modelValue.dimensions.cube"
                      label="Cube">
                  </NumberOnlyInput>
                  <div class="label" style="display: flex; align-items: center;">
                  <span style="margin-left: 10px;">
                      <el-switch
                          v-model="props.modelValue.cubeUnitSwitch"
                          size="small"
                          active-text="feet"
                          inactive-text="meters"
                          style="--el-switch-on-color: #50ac9a; --el-switch-off-color: #50ac9a"/>
                  </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div v-if="props.modelValue.tileSelection.documents" class="cmp-multi-booking_drop-down">
          <div class="cmp-multi-booking_input-container cmp-multi-booking_add-border">
            <i class="fa-thin fa-folder-upload" style="font-size: 40px; color: #ecbf50">📂</i>
            <div class="cmp-multi-booking_upload-area-wrapper">
              <div class="cmp-multi-booking_upload-area-container">
                <div class="cmp-multi-booking_upload-button-area"><span class="label">Upload Documents</span>
                  <span class="cmp-multi-booking_dims-message"> Accepted file types: PDF, png, txt</span>
                  <div style="margin-top: 20px">
                    <Button text="Select" @click="handleUpload"></Button>
                    <span style="margin-left: 20px; font-size: 11px;">
                    You may add multiple files by using ctrl and click in the file window</span>
                  </div>
                  <div v-if="uploadFileSizeError" class="cmp-multi-booking_file-error-area">
                    {{ uploadFileSizeErrorMessage }}
                  </div>
                </div>
                <input :key="updateFileInput" v-show="false" ref="uploadButton" type="file" accept=".txt,.pdf,.png"
                       multiple @change="handleUploadedFiles($event.target.files)"/>
                <div class="cmp-multi-booking_uploaded-file-list-area">
                  <span class="label">Current Documents</span>
<!--                  <span class="cmp-multi-booking_dims-message">-->
<!--                    <span style="font-size: 0px; color: darkred;">&#735</span>-->
<!--                  Removes from the list</span>-->
                  <div class="cmp-multi-booking_uploaded-files-list">
                    <div v-for="(item, index) in props.modelValue.files" class="cmp-multi-booking_uploaded-files-items">
                      {{ item.fileName }}
                      <div @click="handleRemoveDocument(index)">
                        <div title="REMOVE" style="font-size: 30px; margin-top: 13px; color: darkred; cursor: pointer">&#735</div>
                      </div>
                    </div>
                    <div v-if="props.modelValue.files.length === 0" class="global-no-data-placeholder"
                         style="display: flex; align-items: center; justify-content: center; height: 130px; font-size: 12px;">
                      No Current Documents
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <!--          <div class="cmp-multi-booking_drop-down-buttons">-->
          <!--            <Button @click="handleTileSelection('documents')" text="Close" type="toolbar"></Button>-->
          <!--          </div>-->
        </div>
      </div>
      <div class="cmp-multi-booking_summary-section">
        <StaticWidget title="Booking Summary" height="250" background="#fdfdfd">
            <div class="cmp-multi-booking_summary-wrapper">
            <div>
              <BookingSummaryDisplay displayType="address" :highlight="tileColor">
                <template #icon></template>
                <template #title>Shipper</template>
                <template v-if="props.modelValue.shipper.shipperName" #heading>
                  <div>{{ props.modelValue.shipper.shipperName.substring(0, 35) ?? '' }}
                    <span>
                      <StaticTooltip :icon="false">
                        <template #content>
                          <div style="min-width: 100px"><div
                              style="color: var(--aqua-blue-definition); margin-bottom: 5px;">Address Information</div>
                            <div><strong>{{ props.modelValue.shipper.shipperName }}</strong></div>
                            <div>{{ props.modelValue.shipper.shipperAddressLine1 }}</div>
                            <div>{{ props.modelValue.shipper.shipperAddressLine2 }}</div>
                            <div>{{ props.modelValue.shipper.shipperCity }}</div>
                            <div>{{ props.modelValue.shipper.shipperPostcode }}</div>
                            <div>{{ props.modelValue.shipper.shipperCounty }}</div>
                            <div>{{ props.modelValue.shipper.shipperCountry }}</div>
                            <div style="margin-top: 3px; color: #696969;">VAT No. {{
                                props.modelValue.shipper.shipperVatNumber
                              }}</div>
                          </div>
                        </template>
              <i :class="store.state.globalHelpIcon" style="margin-left: 6px;"></i>
                    </StaticTooltip>
                  </span>
                  </div>
                </template>
                <template #second-content v-if="props.modelValue.shipper.shipperContactFirstName?.substring(0, 30)">
                  <span><img height="12" width="12" src="@/assets/icons/user_small.png"></span>
                  <span> {{
                      props.modelValue.shipper.shipperContactFirstName + ' '
                      + props.modelValue.shipper.shipperContactLastName
                    }}</span>
                </template>
                <template #third-content v-if="props.modelValue.shipper.shipperContactPhone">
                  <span style="margin-top: 3px"> <img height="10" width="10" src="@/assets/icons/phone_small.png"></span>
                  <span> {{ props.modelValue.shipper.shipperContactPhone }}</span>
                </template>
                <template #fourth-content v-if="props.modelValue.shipper.shipperContactEmail">
                  <span> <img height="12" width="12" src="@/assets/icons/email_small.png"></span>
                  <span> {{ props.modelValue.shipper.shipperContactEmail }}</span>
                </template>
                <template #placeholder v-if="!useCheckFieldsForData(props.modelValue.shipper)">
                  <div style="margin-top: 14px;"><img :src="getPlaceholder"/></div>
                </template>
              </BookingSummaryDisplay>
            </div>
            <div>
              <BookingSummaryDisplay displayType="address" :highlight="tileColor">
                <template #icon><i class="fa-duotone fa-clipboard-check" style="margin-left: 5px;"></i></template>
                <template #title>Consignee</template>
                <template v-if="props.modelValue.consignee.consigneeName" #heading>
                  <div>{{ props.modelValue.consignee.consigneeName.substring(0, 35) ?? '' }}
                    <span>
                      <StaticTooltip :icon="false">
                  <template #content>
                    <div style="min-width: 100px"><div style="color: var(--aqua-blue-definition); margin-bottom: 5px;">Address Information</div>
                      <div><strong>{{ props.modelValue.consignee.consigneeName }}</strong></div>
                      <div>{{ props.modelValue.consignee.consigneeAddressLine1 }}</div>
                      <div>{{ props.modelValue.consignee.consigneeAddressLine2 }}</div>
                      <div>{{ props.modelValue.consignee.consigneeCity }}</div>
                      <div>{{ props.modelValue.consignee.consigneePostcode }}</div>
                      <div>{{ props.modelValue.consignee.consigneeCounty }}</div>
                      <div>{{ props.modelValue.consignee.consigneeCountry }}</div>
                      <div style="margin-top: 3px; color: #696969;">VAT No. {{
                          props.modelValue.consignee.consigneeVatNumber
                        }}</div>
                    </div>
                  </template>
              <i :class="store.state.globalHelpIcon" style="margin-left: 6px;"></i>
                </StaticTooltip>
                  </span>
                  </div>
                </template>
                <template #second-content v-if="props.modelValue.consignee.consigneeContactFirstName">
                  <span><img height="12" width="12" src="@/assets/icons/user_small.png"></span>
                  <span> {{
                      props.modelValue.consignee.consigneeContactFirstName + ' '
                      + props.modelValue.consignee.consigneeContactLastName
                    }}</span>
                </template>
                <template #third-content v-if="props.modelValue.consignee.consigneeContactPhone">
                  <span style="margin-top: 3px"> <img height="10" width="10" src="@/assets/icons/phone_small.png"></span>
                  <span> {{ props.modelValue.consignee.consigneeContactPhone }}</span>
                </template>
                <template #fourth-content v-if="props.modelValue.consignee.consigneeContactEmail">
                  <span> <img height="12" width="12" src="@/assets/icons/email_small.png"></span>
                  <span> {{ props.modelValue.consignee.consigneeContactEmail }}</span>
                </template>
                <template #placeholder v-if="!useCheckFieldsForData(props.modelValue.consignee)">
                  <div style="margin-top: 14px;"><img :src="getPlaceholder"/></div>
                </template>
              </BookingSummaryDisplay>
            </div>
          </div>
          <div style="margin-left: 20px;">
            <div v-if="route.params.section === 'Air'">
              <BookingSummaryDisplay :width="1500" :highlight="tileColor">
                <template #icon><i class="fa-duotone fa-person-dolly-empty" style="margin-left: 5px;"></i></template>
                <template #title>Shipment</template>
                <template #1>
                  <div style="display: flex; gap:50px;">
                    <div>
                      <div class="cmp-multi-booking_summary-label" style="padding: 20px 10px 2px 0;">Customer Reference
                      </div>
                      <div class="cmp-multi-booking_summary-label cmp-multi-booking_summary-data"
                           v-if="props.modelValue.flightShipment.customerRef">

                        <StaticTooltip :icon="false">
                          <template #content>
                            <div style="max-width: 400px;">{{ props.modelValue.flightShipment.customerRef }}</div>
                          </template>
                          {{ useCreateSubstringText(props.modelValue.flightShipment.customerRef, 35) }}
                        </StaticTooltip>
                      </div>
                      <div v-else class="cmp-multi-booking_summary-label cmp-multi-booking_summary-data">
                        <div class="data-placeholder"></div>
                      </div>
                    </div>
                  </div>
                </template>
                <template #2>
                  <div style="display: flex; gap:50px;">
                    <div>
                      <div class="cmp-multi-booking_summary-label" style="padding: 20px 10px 2px 0;">Commodity</div>
                      <div class="cmp-multi-booking_summary-label cmp-multi-booking_summary-data"
                           v-if="props.modelValue.flightShipment.commodity">
                        <StaticTooltip :icon="false">
                          <template #content>
                            <div style="max-width: 400px;">{{ props.modelValue.flightShipment.commodity }}</div>
                          </template>
                          {{ useCreateSubstringText(props.modelValue.flightShipment.commodity, 35) }}
                        </StaticTooltip>
                      </div>
                      <div v-else class="cmp-multi-booking_summary-label cmp-multi-booking_summary-data">
                        <div class="data-placeholder"></div>
                      </div>
                    </div>
                  </div>

                </template>
                <template #3>
                  <div style="display: flex; gap:50px;">
                    <div>
                      <div class="cmp-multi-booking_summary-label" style="padding: 20px 10px 2px 0;">Remarks</div>
                      <div class="cmp-multi-booking_summary-label cmp-multi-booking_summary-data"
                           v-if="props.modelValue.flightShipment.remarks">
                        <StaticTooltip :icon="false">
                          <template #content>
                            <div style="max-width: 400px;">{{ props.modelValue.flightShipment.remarks }}</div>
                          </template>
                          {{ useCreateSubstringText(props.modelValue.flightShipment.remarks, 35) }}
                        </StaticTooltip>
                      </div>
                      <div v-else class="cmp-multi-booking_summary-label cmp-multi-booking_summary-data">
                        <div class="data-placeholder"></div>
                      </div>
                    </div>
                  </div>
                </template>
                <template #4>
                  <div style="display: flex; gap:50px;">
                    <div>
                      <div class="cmp-multi-booking_summary-label" style="padding: 20px 0 2px 0;">Value
                      </div>
                      <div class="cmp-multi-booking_summary-label cmp-multi-booking_summary-data"
                           v-if="props.modelValue.flightShipment.shipmentValue">
                        {{ props.modelValue.flightShipment.shipmentValue }}
                      </div>
                      <div v-else class="cmp-multi-booking_summary-label cmp-multi-booking_summary-data">
                        <div class="data-placeholder" style="width: 100px;"></div>
                      </div>
                    </div>
                    <div>
                      <div class="cmp-multi-booking_summary-label" style="padding: 20px 10px 2px 0;">Currency
                      </div>
                      <div class="cmp-multi-booking_summary-label cmp-multi-booking_summary-data"
                           v-if="props.modelValue.flightShipment.currency">
                        {{ props.modelValue.flightShipment.currency }}
                      </div>
                      <div v-else class="cmp-multi-booking_summary-label cmp-multi-booking_summary-data">
                        <div class="data-placeholder" style="width: 100px;"></div>
                      </div>
                    </div>
                  </div>
                </template>
              </BookingSummaryDisplay>
            </div>
            <div v-if="route.params.section === 'Air'">
              <BookingSummaryDisplay :width="1500" :highlight="tileColor">
                <template #icon><i class="fa-duotone fa-truck-ramp-box"></i></template>
                <template #title>Movement</template>
                <template #1>
                  <div style="display: flex; gap:50px;">
                    <div>
                      <div class="cmp-multi-booking_summary-label" style="padding: 20px 10px 2px 0;">Place Of Reciept
                      </div>
                      <div class="cmp-multi-booking_summary-label cmp-multi-booking_summary-data"
                           v-if="props.modelValue.flightShipment.placeOfReceipt">
                        {{ props.modelValue.flightShipment.placeOfReceipt }}
                      </div>
                      <div v-else class="cmp-multi-booking_summary-label cmp-multi-booking_summary-data">
                        <div class="data-placeholder"></div>
                      </div>
                    </div>
                  </div>
                </template>
                <template #2>
                  <div class="cmp-multi-booking_summary-label" style="padding: 20px 10px 2px 0;">Origin Port</div>
                  <div class="cmp-multi-booking_summary-label cmp-multi-booking_summary-data"
                       v-if="props.modelValue.flightShipment.originPort">
                    {{ props.modelValue.flightShipment.originPort }}
                  </div>
                  <div v-else class="cmp-multi-booking_summary-label cmp-multi-booking_summary-data">
                    <div class="data-placeholder"></div>
                  </div>
                </template>
                <template #3>
                  <div style="display: flex; gap:50px;">
                    <div>
                      <div class="cmp-multi-booking_summary-label" style="padding: 20px 10px 2px 0;">Destination Port
                      </div>
                      <div class="cmp-multi-booking_summary-label cmp-multi-booking_summary-data" style="width: 100px"
                           v-if="props.modelValue.flightShipment.destinationPort">
                        {{ props.modelValue.flightShipment.destinationPort }}
                      </div>
                      <div v-else class="cmp-multi-booking_summary-label cmp-multi-booking_summary-data">
                        <div class="data-placeholder" style="width:100px"></div>
                      </div>
                    </div>
                  </div>
                </template>
                <template #4>
                  <div style="display: flex; gap:50px;">
                    <div>
                      <div class="cmp-multi-booking_summary-label" style="padding: 20px 10px 2px 0;">Est Pickup Date
                      </div>
                      <div class="cmp-multi-booking_summary-label cmp-multi-booking_summary-data" style="width: 100px"
                           v-if="props.modelValue.flightShipment.estimatedPickupDate">
                        {{ props.modelValue.flightShipment.estimatedPickupDate }}
                      </div>
                      <div v-else class="cmp-multi-booking_summary-label cmp-multi-booking_summary-data">
                        <div class="data-placeholder" style="width:100px"></div>
                      </div>
                    </div>
                    <div>
                      <div class="cmp-multi-booking_summary-label" style="padding: 20px 10px 2px 0;">Est Delivery Date
                      </div>
                      <div class="cmp-multi-booking_summary-label cmp-multi-booking_summary-data"
                           v-if="props.modelValue.flightShipment.estimatedDeliveryDate">
                        {{ props.modelValue.flightShipment.estimatedDeliveryDate }}
                      </div>
                      <div v-else class="cmp-multi-booking_summary-label cmp-multi-booking_summary-data">
                        <div class="data-placeholder" style="width:100px"></div>
                      </div>
                    </div>
                  </div>
                </template>
              </BookingSummaryDisplay>
            </div>
            <div v-if="route.params.section === 'Sea'">
              <BookingSummaryDisplay :width="1500" :highlight="tileColor">
                <template #icon><i class="fa-duotone fa-person-dolly-empty" style="margin-left: 5px;"></i></template>
                <template #title>Shipment</template>
                <template #1>
                  <div style="display: flex; gap:50px;">
                    <div>
                      <div class="cmp-multi-booking_summary-label" style="padding: 20px 10px 2px 0;">Customer Reference
                      </div>
                      <div class="cmp-multi-booking_summary-label cmp-multi-booking_summary-data"
                           v-if="props.modelValue.seaShipment?.customerRef">
                        {{ useCreateSubstringText(props.modelValue.seaShipment?.customerRef, 35) }}
                        <StaticTooltip :icon="false">
                          <template #content>
                            <div style="max-width: 400px;">{{ props.modelValue.seaShipment?.customerRef }}</div>
                          </template>
                          <span><i class="fa fa-info-circle"
                                   style="color: var(--info-icon-color); margin-left: 6px;"></i></span>
                        </StaticTooltip>
                      </div>
                      <div v-else class="cmp-multi-booking_summary-label cmp-multi-booking_summary-data">
                        <div class="data-placeholder"></div>
                      </div>
                    </div>
                  </div>
                </template>
                <template #2>
                  <div style="display: flex; gap:50px;">
                    <div>
                      <div class="cmp-multi-booking_summary-label" style="padding: 20px 10px 2px 0;">Commodity</div>
                      <div class="cmp-multi-booking_summary-label cmp-multi-booking_summary-data"
                           v-if="props.modelValue.seaShipment?.commodity">
                        {{ useCreateSubstringText(props.modelValue.seaShipment?.commodity, 35) }}
                        <StaticTooltip :icon="false">
                          <template #content>
                            <div style="max-width: 400px;">{{ props.modelValue.seaShipment?.commodity }}</div>
                          </template>
                          <span><i class="fa fa-info-circle"
                                   style="color: var(--info-icon-color); margin-left: 6px;"></i></span>
                        </StaticTooltip>
                      </div>
                      <div v-else class="cmp-multi-booking_summary-label cmp-multi-booking_summary-data">
                        <div class="data-placeholder"></div>
                      </div>
                    </div>
                  </div>

                </template>
                <template #3>
                  <div style="display: flex; gap:50px;">
                    <div>
                      <div class="cmp-multi-booking_summary-label" style="padding: 20px 10px 2px 0;">Marks & Numbers
                      </div>
                      <div class="cmp-multi-booking_summary-label cmp-multi-booking_summary-data"
                           v-if="props.modelValue.seaShipment?.marksNumbers">
                        {{ useCreateSubstringText(props.modelValue.seaShipment?.marksNumbers, 35) }}
                        <StaticTooltip :icon="false">
                          <template #content>
                            <div style="max-width: 400px;">{{ props.modelValue.seaShipment?.marksNumbers }}</div>
                          </template>
                          <span><i class="fa fa-info-circle"
                                   style="color: var(--info-icon-color); margin-left: 6px;"></i></span>
                        </StaticTooltip>
                      </div>
                      <div v-else class="cmp-multi-booking_summary-label cmp-multi-booking_summary-data">
                        <div class="data-placeholder"></div>
                      </div>
                    </div>
                  </div>
                </template>
                <template #4>
                  <div style="display: flex; gap:50px;">
                    <div>
                      <div class="cmp-multi-booking_summary-label" style="padding: 20px 0 2px 0;">Value
                      </div>
                      <div class="cmp-multi-booking_summary-label cmp-multi-booking_summary-data" style="width: 100px;"
                           v-if="props.modelValue.seaShipment?.shipmentValue">
                        {{ props.modelValue.seaShipment.shipmentValue }}
                      </div>
                      <div v-else class="cmp-multi-booking_summary-label cmp-multi-booking_summary-data">
                        <div class="data-placeholder" style="width: 100px;"></div>
                      </div>
                    </div>
                    <div>
                      <div class="cmp-multi-booking_summary-label" style="padding: 20px 10px 2px 0;">Currency
                      </div>
                      <div class="cmp-multi-booking_summary-label cmp-multi-booking_summary-data"
                           v-if="props.modelValue.seaShipment?.currency">{{ props.modelValue.seaShipment.currency }}
                      </div>
                      <div v-else class="cmp-multi-booking_summary-label cmp-multi-booking_summary-data">
                        <div class="data-placeholder" style="width: 100px;"></div>
                      </div>
                    </div>
                  </div>
                </template>
              </BookingSummaryDisplay>
            </div>
            <div v-if="route.params.section === 'Sea'">
              <BookingSummaryDisplay :width="1500" :highlight="tileColor">
                <template #icon><i class="fa-duotone fa-truck-ramp-box"></i></template>
                <template #title>Movement</template>
                <template #1>
                  <div style="display: flex; gap:50px;">
                    <div>
                      <div class="cmp-multi-booking_summary-label" style="padding: 20px 10px 2px 0;">Place Of Receipt
                      </div>
                      <div class="cmp-multi-booking_summary-label cmp-multi-booking_summary-data"
                           v-if="props.modelValue.seaShipment.placeOfReceipt">
                        {{ props.modelValue.seaShipment.placeOfReceipt }}
                      </div>
                      <div v-else class="cmp-multi-booking_summary-label cmp-multi-booking_summary-data">
                        <div class="data-placeholder"></div>
                      </div>
                    </div>
                  </div>
                </template>
                <template #2>
                  <div class="cmp-multi-booking_summary-label" style="padding: 20px 10px 2px 0;">Origin Port</div>
                  <div class="cmp-multi-booking_summary-label cmp-multi-booking_summary-data"
                       v-if="props.modelValue.seaShipment.originPort">
                    {{ props.modelValue.seaShipment.originPort }}
                  </div>
                  <div v-else class="cmp-multi-booking_summary-label cmp-multi-booking_summary-data">
                    <div class="data-placeholder"></div>
                  </div>
                </template>
                <template #3>
                  <div style="display: flex; gap:50px;">
                    <div>
                      <div class="cmp-multi-booking_summary-label" style="padding: 20px 10px 2px 0;">Destination Port
                      </div>
                      <div class="cmp-multi-booking_summary-label cmp-multi-booking_summary-data" style="width: 100px"
                           v-if="props.modelValue.seaShipment.destinationPort">
                        {{ props.modelValue.seaShipment.destinationPort }}
                      </div>
                      <div v-else class="cmp-multi-booking_summary-label cmp-multi-booking_summary-data">
                        <div class="data-placeholder" style="width:100px"></div>
                      </div>
                    </div>
                  </div>
                </template>
                <template #4>
                  <div style="display: flex; gap:50px;">
                    <div>
                      <div class="cmp-multi-booking_summary-label" style="padding: 20px 10px 2px 0;">Est Pickup Date
                      </div>
                      <div class="cmp-multi-booking_summary-label cmp-multi-booking_summary-data" style="width: 100px"
                           v-if="props.modelValue.seaShipment.estimatedPickupDate">
                        {{ props.modelValue.seaShipment.estimatedPickupDate }}
                      </div>
                      <div v-else class="cmp-multi-booking_summary-label cmp-multi-booking_summary-data">
                        <div class="data-placeholder" style="width:100px"></div>
                      </div>
                    </div>
                    <div>
                      <div class="cmp-multi-booking_summary-label" style="padding: 20px 10px 2px 0;">Est Delivery Date
                      </div>
                      <div class="cmp-multi-booking_summary-label cmp-multi-booking_summary-data"
                           v-if="props.modelValue.seaShipment.estimatedDeliveryDate">
                        {{ props.modelValue.seaShipment.estimatedDeliveryDate }}
                      </div>
                      <div v-else class="cmp-multi-booking_summary-label cmp-multi-booking_summary-data">
                        <div class="data-placeholder" style="width:100px"></div>
                      </div>
                    </div>
                  </div>
                </template>
              </BookingSummaryDisplay>
            </div>
            <div v-if="route.params.section === 'Road'">
              <BookingSummaryDisplay :width="1500" :highlight="tileColor">
                <template #icon><i class="fa-duotone fa-person-dolly-empty" style="margin-left: 5px;"></i></template>
                <template #title>Shipment</template>
                <template #1>
                  <div style="display: flex; gap:50px;">
                    <div>
                      <div class="cmp-multi-booking_summary-label" style="padding: 20px 10px 2px 0;">Customer Reference
                      </div>
                      <div class="cmp-multi-booking_summary-label cmp-multi-booking_summary-data"
                           v-if="props.modelValue.roadShipment?.customerRef">
                        {{ useCreateSubstringText(props.modelValue.roadShipment?.customerRef, 35) }}
                        <StaticTooltip :icon="false">
                          <template #content>
                            <div style="max-width: 400px;">{{ props.modelValue.roadShipment?.customerRef }}</div>
                          </template>
                          <span><i class="fa fa-info-circle"
                                   style="color: var(--info-icon-color); margin-left: 6px;"></i></span>
                        </StaticTooltip>
                      </div>
                      <div v-else class="cmp-multi-booking_summary-label cmp-multi-booking_summary-data">
                        <div class="data-placeholder"></div>
                      </div>
                    </div>
                  </div>
                </template>
                <template #2>
                  <div style="display: flex; gap:50px;">
                    <div>
                      <div class="cmp-multi-booking_summary-label" style="padding: 20px 10px 2px 0;">Seal Number</div>
                      <div class="cmp-multi-booking_summary-label cmp-multi-booking_summary-data"
                           v-if="props.modelValue.roadShipment?.sealNumber">
                        {{ useCreateSubstringText(props.modelValue.roadShipment?.sealNumber, 35) }}
                        <StaticTooltip :icon="false">
                          <template #content>
                            <div style="max-width: 400px;">{{ props.modelValue.roadShipment?.sealNumber }}</div>
                          </template>
                          <span><i class="fa fa-info-circle"
                                   style="color: var(--info-icon-color); margin-left: 6px;"></i></span>
                        </StaticTooltip>
                      </div>
                      <div v-else class="cmp-multi-booking_summary-label cmp-multi-booking_summary-data">
                        <div class="data-placeholder"></div>
                      </div>
                    </div>
                  </div>

                </template>
                <template #3>
                  <div style="display: flex; gap:50px;">
                    <div>
                      <div class="cmp-multi-booking_summary-label" style="padding: 20px 10px 2px 0;">Commodity</div>
                      <div class="cmp-multi-booking_summary-label cmp-multi-booking_summary-data"
                           v-if="props.modelValue.roadShipment?.commodity">
                        {{ useCreateSubstringText(props.modelValue.roadShipment?.commodity, 35) }}
                        <StaticTooltip :icon="false">
                          <template #content>
                            <div style="max-width: 400px;">{{ props.modelValue.roadShipment?.commodity }}</div>
                          </template>
                          <span><i class="fa fa-info-circle"
                                   style="color: var(--info-icon-color); margin-left: 6px;"></i></span>
                        </StaticTooltip>
                      </div>
                      <div v-else class="cmp-multi-booking_summary-label cmp-multi-booking_summary-data">
                        <div class="data-placeholder"></div>
                      </div>
                    </div>
                  </div>
                </template>
                <template #4>
                  <div style="display: flex; gap:50px;">
                    <div>
                      <div class="cmp-multi-booking_summary-label" style="padding: 20px 0 2px 0;">Value
                      </div>
                      <div class="cmp-multi-booking_summary-label cmp-multi-booking_summary-data" style="width: 100px;"
                           v-if="props.modelValue.roadShipment?.shipmentValue">
                        {{ props.modelValue.roadShipment?.shipmentValue }}
                      </div>
                      <div v-else class="cmp-multi-booking_summary-label cmp-multi-booking_summary-data">
                        <div class="data-placeholder" style="width: 100px;"></div>
                      </div>
                    </div>
                    <div>
                      <div class="cmp-multi-booking_summary-label" style="padding: 20px 10px 2px 0;">Currency
                      </div>
                      <div class="cmp-multi-booking_summary-label cmp-multi-booking_summary-data"
                           v-if="props.modelValue.roadShipment?.currency">{{ props.modelValue.roadShipment.currency }}
                      </div>
                      <div v-else class="cmp-multi-booking_summary-label cmp-multi-booking_summary-data">
                        <div class="data-placeholder" style="width: 100px;"></div>
                      </div>
                    </div>
                  </div>
                </template>
              </BookingSummaryDisplay>
            </div>
            <div v-if="route.params.section === 'Road'">
              <BookingSummaryDisplay :width="1500" :highlight="tileColor">
                <template #icon><i class="fa-duotone fa-truck-ramp-box"></i></template>
                <template #title>Movement</template>
                <template #1>
                  <div style="display: flex; gap:50px;">
                    <div>
                      <div class="cmp-multi-booking_summary-label" style="padding: 20px 10px 2px 0;">Place Of Receipt
                      </div>
                      <div class="cmp-multi-booking_summary-label cmp-multi-booking_summary-data"
                           v-if="props.modelValue.roadShipment?.placeOfReceipt">
                        {{ props.modelValue.roadShipment?.placeOfReceipt }}
                      </div>
                      <div v-else class="cmp-multi-booking_summary-label cmp-multi-booking_summary-data">
                        <div class="data-placeholder"></div>
                      </div>
                    </div>
                  </div>
                </template>
                <template #2>
                  <div class="cmp-multi-booking_summary-label" style="padding: 20px 10px 2px 0;">Origin Port</div>
                  <div class="cmp-multi-booking_summary-label cmp-multi-booking_summary-data"
                       v-if="props.modelValue.roadShipment?.originPort">
                    {{ props.modelValue.roadShipment?.originPort }}
                  </div>
                  <div v-else class="cmp-multi-booking_summary-label cmp-multi-booking_summary-data">
                    <div class="data-placeholder"></div>
                  </div>
                </template>
                <template #3>
                  <div style="display: flex; gap:50px;">
                    <div>
                      <div class="cmp-multi-booking_summary-label" style="padding: 20px 10px 2px 0;">Destination Port
                      </div>
                      <div class="cmp-multi-booking_summary-label cmp-multi-booking_summary-data"
                           v-if="props.modelValue.roadShipment?.destinationPort">
                        {{ props.modelValue.roadShipment?.destinationPort }}
                      </div>
                      <div v-else class="cmp-multi-booking_summary-label cmp-multi-booking_summary-data">
                        <div class="data-placeholder"></div>
                      </div>
                    </div>
                  </div>
                </template>
                <template #4>
                  <div style="display: flex; gap:50px;">
                    <div>
                      <div class="cmp-multi-booking_summary-label" style="padding: 20px 10px 2px 0;">Est Pickup Date
                      </div>
                      <div class="cmp-multi-booking_summary-label cmp-multi-booking_summary-data" style="width: 100px"
                           v-if="props.modelValue.roadShipment?.estimatedPickupDate">
                        {{ props.modelValue.roadShipment?.estimatedPickupDate }}
                      </div>
                      <div v-else class="cmp-multi-booking_summary-label cmp-multi-booking_summary-data">
                        <div class="data-placeholder" style="width:100px"></div>
                      </div>
                    </div>
                    <div>
                      <div class="cmp-multi-booking_summary-label" style="padding: 20px 10px 2px 0;">Est Delivery Date
                      </div>
                      <div class="cmp-multi-booking_summary-label cmp-multi-booking_summary-data"
                           v-if="props.modelValue.roadShipment?.estimatedDeliveryDate">
                        {{ props.modelValue.roadShipment?.estimatedDeliveryDate }}
                      </div>
                      <div v-else class="cmp-multi-booking_summary-label cmp-multi-booking_summary-data">
                        <div class="data-placeholder" style="width:100px"></div>
                      </div>
                    </div>
                  </div>
                </template>
              </BookingSummaryDisplay>
            </div>
            <div v-if="route.params.section === 'Rail'">
              <BookingSummaryDisplay :width="1500" :highlight="tileColor">
                <template #icon><i class="fa-duotone fa-person-dolly-empty" style="margin-left: 5px;"></i></template>
                <template #title>Shipment</template>
                <template #1>
                  <div style="display: flex; gap:50px;">
                    <div>
                      <div class="cmp-multi-booking_summary-label" style="padding: 20px 10px 2px 0;">Customer Reference
                      </div>
                      <div class="cmp-multi-booking_summary-label cmp-multi-booking_summary-data"
                           v-if="props.modelValue.railShipment?.customerRef">
                        <StaticTooltip :icon="false">
                          <template #content>
                            <div style="max-width: 400px;">{{ props.modelValue.railShipment?.customerRef }}</div>
                          </template>
                          {{ useCreateSubstringText(props.modelValue.railShipment?.customerRef, 35) }}
                        </StaticTooltip>
                      </div>
                      <div v-else class="cmp-multi-booking_summary-label cmp-multi-booking_summary-data">
                        <div class="data-placeholder"></div>
                      </div>
                    </div>
                  </div>
                </template>
                <template #2>
                  <div style="display: flex; gap:50px;">
                    <div>
                      <div class="cmp-multi-booking_summary-label" style="padding: 20px 10px 2px 0;">Seal Number</div>
                      <div class="cmp-multi-booking_summary-label cmp-multi-booking_summary-data"
                           v-if="props.modelValue.railShipment?.sealNumber">
                        <StaticTooltip :icon="false">
                          <template #content>
                            <div style="max-width: 400px;">{{ props.modelValue.railShipment?.sealNumber }}</div>
                          </template>
                          {{ useCreateSubstringText(props.modelValue.railShipment?.sealNumber, 35) }}
                        </StaticTooltip>
                      </div>
                      <div v-else class="cmp-multi-booking_summary-label cmp-multi-booking_summary-data">
                        <div class="data-placeholder"></div>
                      </div>
                    </div>
                  </div>

                </template>
                <template #3>
                  <div style="display: flex; gap:50px;">
                    <div>
                      <div class="cmp-multi-booking_summary-label" style="padding: 20px 10px 2px 0;">Commodity</div>
                      <div class="cmp-multi-booking_summary-label cmp-multi-booking_summary-data"
                           v-if="props.modelValue.railShipment?.commodity">
                        <StaticTooltip :icon="false">
                          <template #content>
                            <div style="max-width: 400px;">{{ props.modelValue.railShipment?.commodity }}</div>
                          </template>
                          {{ useCreateSubstringText(props.modelValue.railShipment?.commodity, 35) }}
                        </StaticTooltip>
                      </div>
                      <div v-else class="cmp-multi-booking_summary-label cmp-multi-booking_summary-data">
                        <div class="data-placeholder"></div>
                      </div>
                    </div>
                  </div>
                </template>
                <template #4>
                  <div style="display: flex; gap:50px;">
                    <div>
                      <div class="cmp-multi-booking_summary-label" style="padding: 20px 0 2px 0;">Value
                      </div>
                      <div class="cmp-multi-booking_summary-label cmp-multi-booking_summary-data" style="width: 100px;"
                           v-if="props.modelValue.railShipment?.shipmentValue">
                        {{ props.modelValue.railShipment?.shipmentValue }}
                      </div>
                      <div v-else class="cmp-multi-booking_summary-label cmp-multi-booking_summary-data">
                        <div class="data-placeholder" style="width: 100px;"></div>
                      </div>
                    </div>
                    <div>
                      <div class="cmp-multi-booking_summary-label" style="padding: 20px 10px 2px 0;">Currency
                      </div>
                      <div class="cmp-multi-booking_summary-label cmp-multi-booking_summary-data"
                           v-if="props.modelValue.railShipment?.currency">{{ props.modelValue.railShipment.currency }}
                      </div>
                      <div v-else class="cmp-multi-booking_summary-label cmp-multi-booking_summary-data">
                        <div class="data-placeholder" style="width: 100px;"></div>
                      </div>
                    </div>
                  </div>
                </template>
              </BookingSummaryDisplay>
            </div>
            <div v-if="route.params.section === 'Rail'">
              <BookingSummaryDisplay :width="1500" :highlight="tileColor">
                <template #icon><i class="fa-duotone fa-truck-ramp-box"></i></template>
                <template #title>Movement</template>
                <template #1>
                  <div style="display: flex; gap:50px;">
                    <div>
                      <div class="cmp-multi-booking_summary-label" style="padding: 20px 10px 2px 0;">Place Of Receipt
                      </div>
                      <div class="cmp-multi-booking_summary-label cmp-multi-booking_summary-data"
                           v-if="props.modelValue.railShipment?.placeOfReceipt">
                        {{ props.modelValue.railShipment?.placeOfReceipt }}
                      </div>
                      <div v-else class="cmp-multi-booking_summary-label cmp-multi-booking_summary-data">
                        <div class="data-placeholder"></div>
                      </div>
                    </div>
                  </div>
                </template>
                <template #2>
                  <div class="cmp-multi-booking_summary-label" style="padding: 20px 10px 2px 0;">Origin Port</div>
                  <div class="cmp-multi-booking_summary-label cmp-multi-booking_summary-data"
                       v-if="props.modelValue.railShipment?.originPort">
                    {{ props.modelValue.railShipment?.originPort }}
                  </div>
                  <div v-else class="cmp-multi-booking_summary-label cmp-multi-booking_summary-data">
                    <div class="data-placeholder"></div>
                  </div>
                </template>
                <template #3>
                  <div style="display: flex; gap:50px;">
                    <div>
                      <div class="cmp-multi-booking_summary-label" style="padding: 20px 10px 2px 0;">Destination Port
                      </div>
                      <div class="cmp-multi-booking_summary-label cmp-multi-booking_summary-data"
                           v-if="props.modelValue.railShipment?.destinationPort">
                        {{ props.modelValue.railShipment?.destinationPort }}
                      </div>
                      <div v-else class="cmp-multi-booking_summary-label cmp-multi-booking_summary-data">
                        <div class="data-placeholder"></div>
                      </div>
                    </div>
                  </div>
                </template>
                <template #4>
                  <div style="display: flex; gap:50px;">
                    <div>
                      <div class="cmp-multi-booking_summary-label" style="padding: 20px 10px 2px 0;">Est Pickup Date
                      </div>
                      <div class="cmp-multi-booking_summary-label cmp-multi-booking_summary-data" style="width: 100px"
                           v-if="props.modelValue.railShipment?.estimatedPickupDate">
                        {{ props.modelValue.railShipment?.estimatedPickupDate }}
                      </div>
                      <div v-else class="cmp-multi-booking_summary-label cmp-multi-booking_summary-data">
                        <div class="data-placeholder" style="width:100px"></div>
                      </div>
                    </div>
                    <div>
                      <div class="cmp-multi-booking_summary-label" style="padding: 20px 10px 2px 0;">Est Delivery Date
                      </div>
                      <div class="cmp-multi-booking_summary-label cmp-multi-booking_summary-data"
                           v-if="props.modelValue.railShipment?.estimatedDeliveryDate">
                        {{ props.modelValue.railShipment?.estimatedDeliveryDate }}
                      </div>
                      <div v-else class="cmp-multi-booking_summary-label cmp-multi-booking_summary-data">
                        <div class="data-placeholder" style="width:100px"></div>
                      </div>
                    </div>
                  </div>
                </template>
              </BookingSummaryDisplay>
            </div>
          </div>
        </StaticWidget>
      </div>
    </StaticWidget>
    <Modal
        v-model="props.modelValue.openShipperModal"
        :showClose="false"
        @close="closeShipperModel()"
        :title="modelValue.isEditShipperAddress ? 'Edit Shipper': 'New Shipper'"
        width="510px"
        height="420px">
      <template #body>
        <div class="cmp-multi-booking_address-entry global-css_horizontal-form-layout-label-top">
          <TextInput
              v-if="props.modelValue.metaData.validatedFields"
              :maxLength="50"
              :width="400"
              v-model="props.modelValue.newShipperCompany.shipperName"
              v-model:validation-fail="validationViewModel[props.validationViewModelSection].shipperName"
              :requiredField="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'shipperName' )"
              :label-top="true"
              label="Shipper Name">
          </TextInput>
          <TextInput
              :maxLength="50"
              :width="400"
              v-model="props.modelValue.newShipperCompany.shipperRef"
              v-model:validation-fail="validationViewModel[props.validationViewModelSection].shipperRef"
              :requiredField="useGetValidatedFields(props.modelValue.metaData.validatedFields, 'shipperRef' )"
              :label-top="true"
              label="Shipper Ref">
          </TextInput>
          <TextInput
              v-if="props.modelValue.metaData.validatedFields"
              :maxLength="50"
              :width="400"
              v-model="props.modelValue.newShipperCompany.shipperAddressLine1"
              v-model:validation-fail="validationViewModel[props.validationViewModelSection].shipperAddressLine1"
              :requiredField="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'shipperAddressLine1' )"
              :label-top="true"
              label="Address Line 1">
          </TextInput>
          <TextInput
              v-if="props.modelValue.metaData.validatedFields"
              :maxLength="50"
              :width="400"
              v-model="props.modelValue.newShipperCompany.shipperAddressLine2"
              v-model:validation-fail="validationViewModel[props.validationViewModelSection].shipperAddressLine2"
              :requiredField="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'shipperAddressLine2' )"
              :label-top="true"
              label="Address Line 2">
          </TextInput>
          <TextInput
              v-if="props.modelValue.metaData.validatedFields"
              :maxLength="50"
              :width="280"
              v-model="props.modelValue.newShipperCompany.shipperCity"
              v-model:validation-fail="validationViewModel[props.validationViewModelSection].shipperCity"
              :requiredField="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'shipperCity' )"
              :label-top="true"
              label="City">
          </TextInput>
          <TextInput
              v-if="props.modelValue.metaData.validatedFields"
              :maxLength="10"
              v-model="props.modelValue.newShipperCompany.shipperPostcode"
              v-model:validation-fail="validationViewModel[props.validationViewModelSection].shipperPostcode"
              :requiredField="useGetValidatedFields(props.modelValue.metaData.validatedFields, 'shipperPostcode' )"
              :width="280"
              :label-top="true"
              label="Post Code / ZIP">
          </TextInput>
          <TextInput
              v-if="props.modelValue.metaData.validatedFields"
              :maxLength="50"
              v-model="props.modelValue.newShipperCompany.shipperCounty"
              v-model:validation-fail="validationViewModel[props.validationViewModelSection].shipperCounty"
              :requiredField="useGetValidatedFields(props.modelValue.metaData.validatedFields, 'shipperCounty' )"
              :width="280"
              :label-top="true"
              label="County / State">
          </TextInput>
          <MultiSearchDropDown
              v-if="props.modelValue.metaData.countries.length > 0"
              :requiredField="useGetValidatedFields(props.modelValue.metaData.validatedFields, 'shipperCountry' )"
              :allowCustomValue="false"
              placeholder="Select or Search...."
              v-model:validation-fail="validationViewModel[props.validationViewModelSection].shipperCountry"
              label="Country"
              :width="280"
              v-model="props.modelValue.newShipperCompany.shipperCountry"
              :selectPrimitiveValue="true"
              :label-top="true"
              :data-source="props.modelValue.metaData.countries">
          </MultiSearchDropDown>
          <div style="display: flex; align-items: center;">
            <TextInput
                v-if="props.modelValue.metaData.validatedFields"
                :maxLength="50"
                :width="280"
                v-model="props.modelValue.newShipperCompany.shipperVatNumber"
                v-model:validation-fail="validationViewModel[props.validationViewModelSection].shipperVatNumber"
                :requiredField="useGetValidatedFields(props.modelValue.metaData.validatedFields, 'shipperVatNumber' )"
                :label-top="true"
                label="VAT Number">
            </TextInput>
            <div @click="useUnSavedShipperAddress(false, 'newShipperCompany')" class="cmp-multi-booking_address-link">Use This Address</div>
            <div title="Use this address for the booking only, this will not be saved in your shipper list."
                 class="global-info-icon">
            </div>
          </div>
        </div>
      </template>
      <template #footer>
        <Button
            v-if="props.modelValue.newShipperCompany.id !== 0 "
            @click="handleNewCompanySave('newShipperCompany')"
            icon="<i class='fa-light fa-circle-plus'></i>"
            text="Update">
        </Button>
        <Button
            v-if="props.modelValue.newShipperCompany.id === 0 "
            @click="handleNewCompanySave('newShipperCompany')"
            icon="<i class='fa-light fa-floppy-disk'></i>"
            text="Save">
        </Button>
      </template>
    </Modal>

    <Modal
        v-model="props.modelValue.openShipperContactModal"
        :showClose="false"
        :title="modelValue.newShipperCompany.shipperContactId ? 'Edit Shipper Contact': 'New Shipper Contact'"
        width="530px"
        height="200px">
      <template #body>
        <div class="cmp-multi-booking_address-entry global-css_horizontal-form-layout-label-top">
          <div class="global-flex-layout">
            <MultiSearchDropDown
                :key="updateShipperContacts"
                placeholder="Title"
                :maxLength="50"
                :width="60"
                label="Name"
                :label-top="true"
                :labelWidth="85"
                :allowCustomValue="false"
                :disableFilter="true"
                :selectPrimitiveValue="true"
                v-model:validation-fail="validationViewModel[props.validationViewModelSection].shipperContactTitle"
                :requiredField="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'shipperContactTitle' )"
                v-model="props.modelValue.newShipperCompany.shipperContactTitle"
                :data-source="store.state.contactTitleSelection">
            </MultiSearchDropDown>
            <TextInput
                v-if="props.modelValue.metaData.validatedFields"
                :width="140"
                :maxLength="20"
                label="First Name"
                :label-top="true"
                v-model="props.modelValue.newShipperCompany.shipperContactFirstName"
                v-model:validation-fail="validationViewModel[props.validationViewModelSection].shipperContactFirstName"
                :requiredField="true">
            </TextInput>
            <TextInput
                v-if="props.modelValue.metaData.validatedFields"
                :maxLength="25"
                :width="190"
                label="Last Name"
                :label-top="true"
                v-model="props.modelValue.newShipperCompany.shipperContactLastName"
                v-model:validation-fail="validationViewModel[props.validationViewModelSection].shipperContactLastName"
                :requiredField="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'shipperContactLastName' )">
            </TextInput>
            <StaticTooltip :icon="false">
              <template #content>
                Clear Contact Fields
              </template>
              <img @click="useClearShipperContact(props.modelValue.newShipperCompany)"
                   height="20"
                   width="20"
                   src="@/assets/icons/akar/akar-icons--trash-can.png">
            </StaticTooltip>
          </div>
          <TextInput
              :maxLength="30"
              :labelWidth="85"
              :width="300"
              v-model="props.modelValue.newShipperCompany.shipperContactPhone"
              v-model:validation-fail="validationViewModel[props.validationViewModelSection].shipperContactPhone"
              :requiredField="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'shipperContactPhone' )"
              :label-top="true"
              label="Telephone">
          </TextInput>
          <TextInput
              :maxLength="50"
              :labelWidth="85"
              :width="300"
              v-model="props.modelValue.newShipperCompany.shipperContactEmail"
              :validationText="['Required', 'Invalid Email']"
              v-model:validation-fail="validationViewModel[props.validationViewModelSection].shipperContactEmail"
              :requiredField="useGetValidatedFields(props.modelValue.metaData.validatedFields, 'shipperContactEmail' )"
              :label-top="true"
              label="Email">
          </TextInput>
          <div style="display: flex; align-items: center;">
            <TextInput
                :maxLength="50"
                :labelWidth="85"
                :width="300"
                v-model="props.modelValue.newShipperCompany.shipperContactDepartment"
                v-model:validation-fail="validationViewModel[props.validationViewModelSection].shipperContactDepartment"
                :requiredField="useGetValidatedFields(props.modelValue.metaData.validatedFields, 'shipperContactDepartment' )"
                :label-top="true"
                label="Department">
            </TextInput>
            <div @click="useUnSavedShipperContact()" class="cmp-multi-booking_address-link">Use This Contact</div>
            <div title="Use this contact for the booking only, this will not be saved in your shipper contact list."
                 class="global-info-icon">
            </div>
          </div>
        </div>
      </template>
      <template #footer>
        <Button
            v-if="props.modelValue.newShipperCompany.shipperContactId !== 0"
            @click="saveNewShipperContact(true)"
            icon="<i class='fa-light fa-circle-plus'></i>"
            text="Update">
        </Button>
        <Button
            v-if="props.modelValue.newShipperCompany.shipperContactId === 0"
            @click="saveNewShipperContact(false)"
            icon="<i class='fa-light fa-floppy-disk'></i>"
            :disabled="props.modelValue.shipper.id === 0"
            title="You may save this contact to any newly created, or selected shipper"
            text="Save">
        </Button>
      </template>
    </Modal>

    <Modal
        v-model="props.modelValue.openConsigneeModal"
        :showClose="false"
        @close="closeConsigneeModel()"
        :title="modelValue.isEditConsigneeAddress ? 'Edit Consignee': 'New Consignee'"
        width="530px"
        height="480px">
      <template #body>
        <div class="cmp-multi-booking_address-entry global-css_horizontal-form-layout-label-top">
          <TextInput
              v-if="props.modelValue.metaData.validatedFields"
              :maxLength="50"
              :width="400"
              v-model="props.modelValue.newConsigneeCompany.consigneeName"
              v-model:validation-fail="validationViewModel[props.validationViewModelSection].consigneeName"
              :requiredField="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'consigneeName' )"
              :label-top="true"
              label="Consignee Name">
          </TextInput>
          <TextInput
              v-if="props.modelValue.metaData.validatedFields"
              :maxLength="50"
              :width="400"
              v-model="props.modelValue.newConsigneeCompany.consigneeAddressLine1"
              v-model:validation-fail="validationViewModel[props.validationViewModelSection].consigneeAddressLine1"
              :requiredField="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'consigneeAddressLine1' )"
              :label-top="true"
              label="Address Line 1">
          </TextInput>
          <TextInput
              v-if="props.modelValue.metaData.validatedFields"
              :maxLength="50"
              :width="400"
              v-model="props.modelValue.newConsigneeCompany.consigneeAddressLine2"
              v-model:validation-fail="validationViewModel[props.validationViewModelSection].consigneeAddressLine2"
              :requiredField="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'consigneeAddressLine2' )"
              :label-top="true"
              label="Address Line 2">
          </TextInput>
          <TextInput
              v-if="props.modelValue.metaData.validatedFields"
              :maxLength="50"
              :width="280"
              v-model="props.modelValue.newConsigneeCompany.consigneeCity"
              v-model:validation-fail="validationViewModel[props.validationViewModelSection].consigneeCity"
              :requiredField="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'consigneeCity' )"
              :label-top="true"
              label="City">
          </TextInput>
          <TextInput
              v-if="props.modelValue.metaData.validatedFields"
              :maxLength="10"
              v-model="props.modelValue.newConsigneeCompany.consigneePostcode"
              v-model:validation-fail="validationViewModel[props.validationViewModelSection].consigneePostcode"
              :requiredField="useGetValidatedFields(props.modelValue.metaData.validatedFields, 'consigneePostcode' )"
              :width="280"
              :label-top="true"
              label="Post Code / ZIP">
          </TextInput>
          <TextInput
              v-if="props.modelValue.metaData.validatedFields"
              :maxLength="50"
              v-model="props.modelValue.newConsigneeCompany.consigneeCounty"
              v-model:validation-fail="validationViewModel[props.validationViewModelSection].consigneeCounty"
              :requiredField="useGetValidatedFields(props.modelValue.metaData.validatedFields, 'consigneeCounty' )"
              :width="280"
              :label-top="true"
              label="County / State">
          </TextInput>
          <MultiSearchDropDown
              v-if="props.modelValue.metaData.countries.length > 0"
              :requiredField="useGetValidatedFields(props.modelValue.metaData.validatedFields, 'consigneeCountry' )"
              :allowCustomValue="false"
              placeholder="Select or Search...."
              v-model:validation-fail="validationViewModel[props.validationViewModelSection].consigneeCountry"
              label="Country"
              :width="280"
              v-model="props.modelValue.newConsigneeCompany.consigneeCountry"
              :selectPrimitiveValue="true"
              :label-top="true"
              :data-source="props.modelValue.metaData.countries">
          </MultiSearchDropDown>
            <TextInput
                v-if="props.modelValue.metaData.validatedFields"
                :maxLength="50"
                :width="280"
                v-model="props.modelValue.newConsigneeCompany.consigneeVatNumber"
                v-model:validation-fail="validationViewModel[props.validationViewModelSection].consigneeVatNumber"
                :requiredField="useGetValidatedFields(props.modelValue.metaData.validatedFields, 'consigneeVatNumber' )"
                :label-top="true"
                label="VAT Number">
            </TextInput>
            <TextInput
                v-model="props.modelValue.newConsigneeCompany.consigneeRef"
                v-model:validation-fail="validationViewModel[props.validationViewModelSection].consigneeRef"
                :maxLength="50"
                :width="280"
                :requiredField="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'consigneeRef' )"
                :label-top="true"
                label="Consignee Ref">
            </TextInput>
            <div class="global-flex-layout">
              <TextInput
                  v-model="props.modelValue.newConsigneeCompany.additionalRef"
                  v-model:validation-fail="validationViewModel[props.validationViewModelSection].additionalRef"
                  :maxLength="50"
                  :width="280"
                  :requiredField="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'additionalRef' )"
                  :label-top="true"
                  label="Additional Ref">
              </TextInput>
              <div @click="useUnSavedConsigneeAddress(false, 'newShipperCompany')" class="cmp-multi-booking_address-link">Use This Address</div>
              <div title="Use this address for the booking only, this will not be saved in your shipper list."
                   class="global-info-icon">
              </div>
            </div>

        </div>
      </template>
      <template #footer>
        <Button
            v-if="props.modelValue.newConsigneeCompany.id !== 0"
            @click="handleNewCompanySave('newConsigneeCompany')"
            icon="<i class='fa-light fa-circle-plus'></i>"
            text="Update">
        </Button>
        <Button
            v-if="props.modelValue.newConsigneeCompany.id === 0 "
            @click="handleNewCompanySave('newConsigneeCompany')"
            icon="<i class='fa-light fa-floppy-disk'></i>"
            text="Save">
        </Button>
      </template>
    </Modal>

    <Modal
        v-model="props.modelValue.openConsigneeContactModal"
        :showClose="false"
        @close="closeShipperModel()"
        :title="modelValue.newConsigneeCompany.consigneeContactId ? 'Edit Consignee Contact': 'New Consignee Contact'"
        width="530px"
        height="160px">
      <template #body>
        <div class="cmp-multi-booking_address-entry global-css_horizontal-form-layout-label-top">
          <div class="global-flex-layout">
            <MultiSearchDropDown
                :key="updateConsigneeContacts"
                placeholder="Title"
                :maxLength="50"
                :width="60"
                label="Name"
                :label-top="true"
                :labelWidth="85"
                :allowCustomValue="false"
                :disableFilter="true"
                :selectPrimitiveValue="true"
                v-model:validation-fail="validationViewModel[props.validationViewModelSection].consigneeContactTitle"
                :requiredField="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'consigneeContactTitle' )"
                v-model="props.modelValue.newConsigneeCompany.consigneeContactTitle"
                :data-source="store.state.contactTitleSelection">
            </MultiSearchDropDown>
            <TextInput
                v-if="props.modelValue.metaData.validatedFields"
                :width="140"
                :maxLength="20"
                label="First Name"
                :label-top="true"
                v-model="props.modelValue.newConsigneeCompany.consigneeContactFirstName"
                v-model:validation-fail="validationViewModel[props.validationViewModelSection].consigneeContactFirstName"
                :requiredField="true">
            </TextInput>
            <TextInput
                v-if="props.modelValue.metaData.validatedFields"
                :maxLength="25"
                :width="190"
                label="Last Name"
                :label-top="true"
                v-model="props.modelValue.newConsigneeCompany.consigneeContactLastName"
                v-model:validation-fail="validationViewModel[props.validationViewModelSection].consigneeContactLastName"
                :requiredField="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'consigneeContactLastName' )">
            </TextInput>
            <StaticTooltip :icon="false">
              <template #content>
                Clear Contact Fields
              </template>
              <img @click="useClearConsigneeContact(props.modelValue.newConsigneeCompany)"
                   height="20"
                   width="20"
                   src="@/assets/icons/akar/akar-icons--trash-can.png">
            </StaticTooltip>
          </div>
          <TextInput
              :maxLength="30"
              :labelWidth="85"
              :width="300"
              v-model="props.modelValue.newConsigneeCompany.consigneeContactPhone"
              v-model:validation-fail="validationViewModel[props.validationViewModelSection].consigneeContactPhone"
              :requiredField="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'consigneeContactPhone' )"
              :label-top="true"
              label="Telephone">
          </TextInput>
          <div style="display: flex; align-items: center;">
            <TextInput
                :maxLength="50"
                :labelWidth="85"
                :width="300"
                v-model="props.modelValue.newConsigneeCompany.consigneeContactEmail"
                :validationText="['Required', 'Invalid Email']"
                v-model:validation-fail="validationViewModel[props.validationViewModelSection].consigneeContactEmail"
                :requiredField="useGetValidatedFields(props.modelValue.metaData.validatedFields, 'consigneeContactEmail' )"
                :label-top="true"
                label="Email">
            </TextInput>
            <div @click="useUnSavedConsigneeContact()" class="cmp-multi-booking_address-link">Use This Contact</div>
            <div title="Use this contact for the booking only, this will not be saved in your shipper contact list."
                 class="global-info-icon">
            </div>
          </div>
        </div>
      </template>
      <template #footer>
        <Button
            v-if="props.modelValue.newConsigneeCompany.consigneeContactId !== 0"
            @click="saveNewConsigneeContact(true)"
            icon="<i class='fa-light fa-circle-plus'></i>"
            text="Update">
        </Button>
        <Button
            v-if="props.modelValue.newConsigneeCompany.consigneeContactId === 0"
            @click="saveNewConsigneeContact(false)"
            :disabled="props.modelValue.consignee.id === 0"
            icon="<i class='fa-light fa-floppy-disk'></i>"
            title="You may save this contact to any newly created, or selected consignee"
            text="Save">
        </Button>
      </template>
    </Modal>
  </div>
</template>

<style lang="scss" scoped>

@import '@/css/booking/multi-booking.scss';

.cmp-multi-booking {

  &_drop-down {
    height: 270px;
    padding-top: 20px;
  }

  &_top-bar {
    height: 40px;
    padding-left: 20px;
    display: flex;
    align-items: center;
    //background-color: var(--body-bg);
    background: #f9fbfc;
    border-radius: var(--rounded-corners);
    border: 1px dotted var(--border-color);
  }

  &_summary-wrapper {
    margin-top: 3px;
  }

  &_summary-section {
    margin: 0;
  }

  &_date-area {
    height: 220px;
    width: 480px;
    padding: 10px;
    //border: 1px solid var(--border-color);
    border-radius: var(--rounded-corners);
    background-color: #f2f8fc;
  }

  &_date-area-headings {
    font-size: 14px;
    font-weight: bold;
    margin-left: 120px;
    gap: 110px;
    color: #5b5b5b;
  }

  &_add-border {
    border-left: 6px solid v-bind(tileColor);
  }
}

</style>
